/**
 * **`refereeIntent`** — the referee entry point for the room's **generic
 * `resolve()`/`resolveActivate()` flow**: a standard or reaction (Overwatch) `request_attack`
 * (a GM `gm_force` wraps one and enters through `./refereeForce.ts`), tech attacks and `request_full_tech`,
 * `request_activate` (incl. the Sensors-gated Scan/Lock On/Bolster tech activates),
 * `request_barrage` (opener + continuation), `request_stabilize`, and `request_overcharge`. It
 * owns the whole composition: the pre-dice legality gate ladder for these intents, the
 * `resolve()`-family invocation over `ctx.dice`, the economy sibling batch fold, the eager
 * post-apply followup chain (post-attack observers, destruction consequences, post-move
 * recompute, `on_action_taken`), and the resolution's broadcast rider — returned as an ordered
 * {@link RefereePlan}, never applied or broadcast itself (that stays the room's job).
 *
 * **What stays room-side, in front of this call** (`backend/src/encounterRoom.ts`'s
 * `resolveIntentAfterReveal`) — deliberately NOT pulled in here:
 *   - the ownership gate (`canResolveIntent`) and `resolutionAttribution` — gate-on-source stays
 *     in front of the referee;
 *   - the pre-dice auto-reveal guard (`resolveIntent`'s own `revealConcealed` call) — it runs
 *     before ANY intent-shape routing, generic or bespoke;
 *   - the held-action lockout, and every BESPOKE intent's own dispatch (Hide/Disengage/Shut
 *     Down/Boot Up/Engage COMP/CON/Mount/Self-Destruct/Grapple break-release-contest/Search/
 *     Deploy Drone-Mine/Detect-Disarm-Detonate Mine/Jockey/Jockey Menu/Shake Off/Ride/
 *     Ride-Dismount/End Attack) — none of those reach this function; they're routed off before
 *     the shared gate ladder even starts;
 *   - **the shared budget / once-per-turn-ledger / granted-activate / actor-status-legality /
 *     mid-attack-lock gates**, run ONCE for every intent that reaches them — INCLUDING
 *     `request_dismount`/`request_eject`/`request_reload`, which then dispatch to
 *     `refereeSharedGateAction` rather than reaching this function. Pulling the whole ladder into
 *     the referee would force those three's dispatch to move too, or force it to fork into "the
 *     shared prefix, twice" — so the seam is drawn right AFTER that dispatch instead: the room
 *     runs the shared ladder once, dispatches the three bespoke verbs off it into their own
 *     referee entry point (which composes ONLY their bridge-specific legality, trusting the
 *     ladder already ran — see that file's header), and calls `refereeIntent` for everything that
 *     reaches past them. `midAttack`/`lock`/`turn`/`cost`/`actionId` are cheap, pure
 *     re-derivations off `(state, intent, overlay)`, so recomputing them here (rather than
 *     threading them from the room's own already-computed copies) costs nothing and keeps the
 *     referee's ctx bounded to `{dice, overlay, checkpointConsumers}` + `actorId`/`releaseCtx`.
 *   - flush/persistence, `broadcastChange`/`broadcastResolution`, per-socket projection, prompt
 *     ROUTING (`sendDecisionPrompt`'s socket + GM backstop), `commandLabel` feed lines.
 *
 * **Byte-identity contract**: every command this function mints is built against a *scratch*
 * `EncounterHistory` (`createScratchExecutor`) over a copy of `state` — the room's real
 * `history.applyCommand` never re-derives or re-rolls anything, it just walks `entries` and
 * re-applies the SAME commands in the SAME order.
 */
import { observeTrackChanges } from "./trackObserver";
import {
  activeAttack,
  canUseRoundAction,
  canUseSceneAction,
  DISENGAGED_STATUS,
  feintedBy,
  GRANT_FREE_QUICK_ACTION_SENTINEL,
  grantedActionAllowanceKey,
  hasStatus,
  HIDDEN_STATUS,
  sideOf,
  weaponInstanceKey,
  worldFor,
  type Affordance,
  type AttackLock,
  type Cell,
  type CombatantId,
  type EarmarkVerb,
  type EncounterCommand,
  type EncounterState,
  type RecordMoveReactionDeclineCommand,
  type ResolutionAbout,
  type ResolvedStep,
} from "@lancer-vtt/domain";
import type {
  EncounterIntent,
  EncounterRejectionCategory,
  RequestActivate,
  RequestAttack,
  RequestBarrage,
  RequestFullTech,
  RequestOvercharge,
  RequestStabilize,
  WireCell,
} from "@lancer-vtt/shared";
import { unwrap } from "@lancer-vtt/shared";
import {
  actionIdForIntent,
  earmarkVerbOf,
  quickTechOptionLedgerId,
  barrageLoadout,
  checkAttackCellLoS,
  checkAttackCellRange,
  checkAttackLoS,
  checkAttackRange,
  checkAttackWeapon,
  checkWeaponMode,
  checkSuperheavySkirmish,
  checkBarrageCommit,
  checkBarrageContinuation,
  checkBarrageOpener,
  checkActivateUnitReach,
  checkFullTechContinuation,
  checkFullTechOpener,
  checkGrantedActionLegality,
  checkJammedBenefitLegality,
  checkLimited,
  checkLoaded,
  checkOrdnance,
  checkOrdnanceEngagement,
  checkOrdnanceWindow,
  checkOverchargeLegality,
  checkOverwatchRange,
  checkRamSize,
  checkAttackStance,
  checkReactionStatusLegality,
  checkStabilizeLegality,
  checkTechRange,
  checkWeaponRecharge,
  checkProfileByState,
  COVERING_FIRE_REACTION,
  hasCoveringFireMarkFrom,
  checkTechAttackRecharge,
  hasEligibleSkirmishRider,
  hasLegalBarrageContinuation,
  hasLegalSkirmishContinuation,
  intentCost,
  isActivateActionLegal,
  deployedActionRefusal,
  isAttackLockContinuation,
  reactionShotGrant,
  isProtocolAction,
  isRiderShot,
  mountKeyOf,
  pairedAuxRiderOf,
  legalTechAttackWeaponIds,
  QUICK_TECH_ACTIVATE_IDS,
  resolveAffordance,
} from "../economy";
import { QUICK_TECH_OPTIONS, quickTechIntent, quickTechOption, type QuickTechRoute } from "../quickTech";
import { checkTechLoS } from "../search";
import { resolveOvercharge } from "../overcharge";
import { resolveStabilize } from "../stabilize";
import {
  diceInputSource,
  resolve,
  resolveActivate,
  resolveActionTakenTrigger,
  resolveDamageTrigger,
  ResolveError,
  selectWeapon,
  weaponModeLatchStep,
  type DamageReport,
  type NeedsDecision,
  type Resolved,
  type ResolveResult,
} from "../resolve";
import { isNeedsDecision, type EffectsConfig } from "../resolve/types";
import { NeedsDecisionSignal } from "../effects";
import { areaShapeOf } from "../resolve/area";
import { isRangedWeaponAttack } from "../resolve/weaponMode";
import { attacksTargets } from "../resolve/weaponOwnDamage";
import { immuneToTechAction, hasBlanketImmunity, cannotMakeAttacks, attackStance } from "../effects/seed";
import { gatherInvadeOptions, offeredInvadeOptions, type LoadedOverlay } from "../effects/loader";
import type { ChooseAtom, EffectAtom } from "../effects/atoms";
import { isReactionPromptId } from "../reactionRegistry";
import { declineCommandsFor, type MoveCheckpointContext } from "../checkpointRegistry";
import type { MovePath } from "../effects/spatialQuery";
import { isSystemTraumaPromptId } from "../tables";
import { isStructureInterceptPromptId } from "../structureIntercept";
import { coverDestroyedObserver, grappleDominanceObserver, revealOnAttackObserver, type AttackObserver } from "../attackObservers";
import type { CoreRule } from "./refereeCommand";
import { MOVE_LISTENER_RECOMPUTERS, SETTLE_RECOMPUTERS } from "../spatialRecompute";
import { resolveWreckConversions } from "../wreckConversion";
import { resolveAssetRemains } from "../assetRemains";
import { resolveDroneSystemLosses } from "../droneDestruction";
import { resolvePilotEjections } from "../pilotBridge";
import { rodeoEndsAtDestruction } from "../rodeo";
import { endOnDamageInto, sourceGoneInto } from "./durationCleanup";
import { raiseMoveDeclared } from "./moveDeclared";
import { damageWitnessReports } from "./moveFollowups";
import { recordingDiceSource, type DiceSource, type RecordingDiceSource } from "../../dice";
import { createEventLoop, type ActionDeclaredEvent, type EventLoop } from "../events/loop";
import { stepsCombatantIds, stepsLandDestroy, stepsMovePosition } from "../resolutionSteps";
import { resolveForcedAttacks } from "./forcedAttacks";
import { observedMoveInto } from "./moveFollowups";
import { runMovement } from "./movement";
import { askPendingReactionMove } from "./reactionMoves";
import { auraAfterResolutionInto, auraBeforeAttackPrePass } from "./auraFollowups";
import { createScratchExecutor, type ScratchExecutor } from "./scratchExecutor";
import type { RefereeCtx, RefereePlan, RefereePlanEntry } from "./types";
import { consumeStatusCommands, economyNow, reactionSpendCommands, RULES_SPEND, spendsThisTurn, type ActionUseEvent, type EconomyCostEvent, type SpendPolicy } from "../events/gameEvents";

/**
 * Marks a `refereeIntent` call as resolving a held action's **release** (`release_prepare`) out of
 * turn, while the `preparer`'s hold is still armed — structurally the same `{preparer}` shape as
 * `backend/src/encounterRoom.ts`'s own room-local `ReleaseContext`, which it passes straight in.
 * Threads down to (a) skip the paid-at-prepare gates (Ordnance window/engagement — the once-
 * per-turn/action-economy skips already happened room-side, before this call), and (b) fold a
 * `drop_prepare` sibling into the resolution's own atomic batch.
 */
export type ReleaseContext = {
  /** The combatant releasing its held action — must equal the resolved intent's own source. */
  preparer: CombatantId;
};

/**
 * The intent shapes that actually reach `refereeIntent` at runtime — every OTHER
 * `EncounterIntent` member is routed off room-side (bespoke dispatch, or `request_move`/
 * `end_attack`/the Dismount-Eject-Reload trio dispatched off the shared gate ladder) before the
 * room ever calls this function. Narrowing the parameter type to this union (rather than the
 * full `EncounterIntent`) lets TS's control-flow narrowing at the room's call site (after all
 * the earlier `if (intent.type === "…") return …` dispatches) pass `intent` through with no
 * cast — and makes any future bespoke carve-out a compile error here until its dispatch is
 * added room-side, not a silent no-op inside this function.
 */
export type GenericResolutionIntent =
  | RequestAttack
  | RequestBarrage
  | RequestActivate
  | RequestStabilize
  | RequestOvercharge
  | RequestFullTech;

/**
 * **Which intent shapes can carry a mid-resolution SPATIAL answer back to the server** — the
 * table behind {@link intentCarriesReactionToward} / {@link recordedReactionToward}.
 *
 * A direction halt is answered by re-submitting the **echoed intent** (see `refereeIntent`'s
 * `needs_decision` branch: `{ ...intent, recordedDice }` — the ORIGINAL wire intent, never a
 * shot synthesized from it) with the picked cell recorded on `reactionToward`. An intent shape
 * with no such field therefore **cannot answer at all: the prompt re-raises forever and the
 * encounter hangs**. `RequestBarrage` hit exactly that, on an utterly ordinary table action (a
 * Hunter Barraging with an Auxiliary melee weapon), before it gained a `reactionToward` slot. The
 * `false` rows below are the shapes that still cannot answer; they degrade rather than hang, which
 * is the belt-and-braces half.
 *
 * **Why a table and not `"reactionToward" in intent`.** At runtime an optional-but-absent field
 * is indistinguishable from a field the type never had — a first submit legitimately omits
 * `reactionToward`. The distinction only exists in the type, so it has to be projected into a
 * value somewhere. **Why not `intent.type === "request_barrage"`**: that is a hand-copied mirror
 * of a type fact, and this codebase has already paid for hand-copied mirrors more than once. The
 * `satisfies` clause makes this one non-forgeable in BOTH directions — adding
 * `reactionToward` to a shape turns its `false` entry into a compile error, and adding a new
 * member to {@link GenericResolutionIntent} turns the missing key into one. Neither can rot
 * silently, which a bare type check could.
 */
const REACTION_TOWARD_CARRIERS = {
  request_attack: true,
  request_barrage: true,
  request_activate: false,
  request_stabilize: false,
  request_overcharge: false,
  request_full_tech: false,
} satisfies {
  [K in GenericResolutionIntent["type"]]: "reactionToward" extends keyof Extract<GenericResolutionIntent, { type: K }> ? true : false;
};

/**
 * Can a direction/cell answer to a mid-resolution spatial halt actually get back to the server on
 * this intent shape? Feeds `EffectsConfig.directionAnswerable`, which makes the halt DEGRADE
 * (walk the authored distance / record an honest no-op, plus a warning) rather than raise a
 * prompt with nowhere to answer into. See {@link REACTION_TOWARD_CARRIERS}.
 */
export function intentCarriesReactionToward(intent: GenericResolutionIntent): boolean {
  return REACTION_TOWARD_CARRIERS[intent.type];
}

/**
 * The recorded spatial answer riding this intent, if its shape has a slot for one at all.
 *
 * Read it — never `intent.reactionToward` directly — anywhere a wire intent is projected into a
 * synthesized `RequestAttack` (the Barrage shot, the Full Tech sub-intent). That keeps the
 * threading and {@link REACTION_TOWARD_CARRIERS} moving together: the day `RequestBarrage` gains
 * a `reactionToward` field, the table's `false` becomes a compile error AND every synthesis site
 * starts forwarding the answer, in one edit. Wiring the field without the threading would
 * re-open the hang while *looking* fixed, which is the failure mode worth designing out.
 */
export function recordedReactionToward(intent: GenericResolutionIntent): WireCell | undefined {
  if (!REACTION_TOWARD_CARRIERS[intent.type]) return undefined;
  return (intent as { reactionToward?: WireCell }).reactionToward;
}

/**
 * `refereeIntent`'s bounded call context — the shared `RefereeCtx` (`dice`/`overlay`/
 * `checkpointConsumers`, the last unused by this generic path — it belongs to `refereeMove`) plus
 * `actorId` (the command-log attribution) and `releaseCtx`. `choices`/`recordedDice` are NOT
 * separate ctx fields: they ride on `intent` itself, exactly like every other `EncounterIntent`.
 */
export type RefereeIntentCtx = RefereeCtx & {
  actorId: string;
  releaseCtx?: ReleaseContext;
  /** The intent's event loop and recording source. {@link refereeIntent} makes one when absent; a caller passes one to read its log. One loop serves one intent. */
  intentLoop?: IntentLoop;
};

/** One intent's event loop over its one recording dice source (`decisions/0212` item 6). */
export type IntentLoop = { loop: EventLoop; recordingDice: RecordingDiceSource };

/**
 * The loop for one intent: its recording source replays `intent.recordedDice` before drawing
 * fresh off `dice`, and the loop draws every die from it, so a halt echoes every die drawn.
 */
export function createIntentLoop(intent: { type: string; recordedDice?: number[] }, dice: DiceSource): IntentLoop {
  const recordingDice = recordingDiceSource(intent.recordedDice ?? [], dice);
  return { loop: createEventLoop({ dice: recordingDice }), recordingDice };
}

/**
 * A rejected referee call — no entries, no state change. Exported rather than file-local because
 * every sibling referee entry point needs the exact same tiny builder.
 */
export function rejected(state: EncounterState, message: string, category: EncounterRejectionCategory): RefereePlan {
  return { entries: [], rejection: { message, category }, finalState: state };
}

/** A `refereeIntent` call whose `resolve()`-family pass needs a mid-resolution decision — the "no entries" halt mode. */
function needsDecisionPlan(state: EncounterState, decision: NeedsDecision, echoedIntent: EncounterIntent): RefereePlan {
  return {
    entries: [],
    needsDecision: {
      who: decision.who,
      promptId: decision.promptId,
      prompt: decision.prompt,
      options: decision.options,
      ...(decision.answerKind !== undefined ? { answerKind: decision.answerKind } : {}),
      ...(decision.incomingDamage ? { incomingDamage: decision.incomingDamage } : {}),
      ...(decision.title !== undefined ? { title: decision.title } : {}),
      ...(decision.summary !== undefined ? { summary: decision.summary } : {}),
      // A domain `Cell[]` assigns straight onto the wire's `WireCell[]`
      // (`shared/src/index.ts`'s `WireCell` doc — same tagged-JSON shape).
      ...(decision.reachableCells ? { reachableCells: decision.reachableCells as WireCell[] } : {}),
      ...(decision.section ? { section: { ...decision.section, placed: decision.section.placed as WireCell[] } } : {}),
      intent: echoedIntent,
    },
    finalState: state,
  };
}

/**
 * Apply one referee-minted **followup** command to the scratch pass, trusting it — it's the
 * referee's own composition, never a client input. Followup entries never carry a
 * `broadcastRider` (only the PRIMARY resolution entry does, applied directly via `exec.apply`
 * below so its rider can be stamped) — so this helper's signature stays at 3 params.
 */
function applyTrusted(exec: ScratchExecutor, command: EncounterCommand, actorId: string): void {
  unwrap(exec.apply(command, actorId, undefined));
}

/**
 * Does `key` answer ANY cross-actor prompt this resolution auto-dismisses a stale modal for on
 * broadcast (reactions, System Trauma, and the structure-loss interception)? Auto-dismiss-site-only.
 *
 * **Exported for the OTHER entry point that raises one of these prompts** — `refereeGmDamage`,
 * whose `routeFinalDamage` call reaches the same `resolveDamageCascade` and so can halt on the
 * same System Trauma pick or structure-loss interception. It stamps its own resolution rider
 * through this predicate rather than re-deriving the union, so the two sites cannot drift when a
 * fourth prompt shape joins. **Still auto-dismiss-site-only**: it must never reach
 * `encounterRoom.ts`'s reaction-routing `defenders` filter, which stays `isReactionPromptId` alone
 * (neither System Trauma nor the interception is a reaction).
 */
export function isCrossActorAnsweredPrompt(key: string): boolean {
  return isReactionPromptId(key) || isSystemTraumaPromptId(key) || isStructureInterceptPromptId(key);
}

/**
 * Does `steps` (a resolved `request_activate`'s own steps) grant `actor` a bonus use of the
 * generic content-authored free-quick-action sentinel?
 */
function grantsFreeQuickAction(steps: readonly ResolvedStep[], actor: CombatantId): boolean {
  const key = grantedActionAllowanceKey(GRANT_FREE_QUICK_ACTION_SENTINEL);
  return steps.some((s) => s.op === "resource_delta" && s.key === key && s.target === actor && s.delta > 0);
}

/** What `attack_resolved`'s rules share: the scratch pass, the log attribution and the attacker. */
type AttackResolvedEdge = { exec: ScratchExecutor; actorId: string; attacker: string };

/** One post-attack observer as a rule: its steps against the scratch state, applied under its own kind. */
function observerRule(o: AttackObserver): CoreRule<AttackResolvedEdge> {
  return {
    id: o.id,
    run: ({ exec, actorId, attacker }) => {
      const steps = o.observe({ state: exec.state, attacker });
      if (steps.length > 0) applyTrusted(exec, { type: "apply_resolved", actorId, steps, kind: o.kind }, actorId);
    },
  };
}

/**
 * `attack_resolved`'s rules, in order: the attacker's own reveal, the cover-destroyed sweep, the
 * grapple sync. The reveal runs before the sweep so an attacker it clears reads as already
 * not-Hidden there, never a double clear (`observerOrder.characterization.test.ts`); the grapple
 * sync is disjoint from both.
 */
export const ATTACK_RESOLVED_RULES: readonly CoreRule<AttackResolvedEdge>[] = [revealOnAttackObserver, coverDestroyedObserver, grappleDominanceObserver].map(observerRule);

/** Raise `attack_resolved` for `attacker` into `loop`, running its rules against the scratch state, each landing its own plan entry. */
function raiseAttackResolved(exec: ScratchExecutor, args: { actorId: string; attacker: string; loop: EventLoop }): void {
  const { actorId, attacker, loop } = args;
  loop.raise({ kind: "attack_resolved", attacker }, { trigger: () => ATTACK_RESOLVED_RULES.forEach((r) => r.run({ exec, actorId, attacker })) });
}

/**
 * What a move sets off (D15): `moved`'s recompute listeners for `moved` (the drag reads `from`, when
 * the move has one), then the settle pass. Each landed recomputer is its own plan entry under
 * `spatial_<id>`. Shared by `moved`'s rules (`./movement.ts`), teleport and place
 * (`refereeCommand.ts`), and a displacement inside a resolution or a fired clause (a knockback, a
 * pull: no `from`, no hazards).
 */
export function recomputeAfterMoveInto(exec: ScratchExecutor, actorId: string, args: { moved: string; from?: Cell }): void {
  const { moved, from } = args;
  for (const r of MOVE_LISTENER_RECOMPUTERS) {
    const steps = r.recompute({ state: exec.state, moved, ...(from !== undefined ? { from } : {}) });
    if (steps.length === 0) continue;
    unwrap(exec.apply({ type: "apply_resolved", actorId, steps, kind: `spatial_${r.id}` }, actorId, grappleMoveRider({ recomputerId: r.id, steps, actorId, moved })));
  }
  settleInto(exec, actorId, moved);
}

/** The settle pass (D15): re-sync the derived state ({@link SETTLE_RECOMPUTERS}), each landed recomputer its own `spatial_<id>` entry. */
export function settleInto(exec: ScratchExecutor, actorId: string, moved: string): void {
  for (const r of SETTLE_RECOMPUTERS) {
    const steps = r.recompute({ state: exec.state, moved });
    if (steps.length === 0) continue;
    unwrap(exec.apply({ type: "apply_resolved", actorId, steps, kind: `spatial_${r.id}` }, actorId, undefined));
  }
}

/**
 * The post-move recomputes the table should hear about get a resolution rider, so they reach the
 * COMMS feed and the board as a card: the grapple drag (`grapple_drag`, whose `forced_move` the
 * board then slides rather than blinks) and a grapple ended by broken adjacency (`grapple_end`).
 * Every other recompute (engagement, the Immobilized sync, the shield) stays a plain change.
 * Game night 2026-09-30: the Berserker walked out of a grapple and nothing said so
 * (`projects/grapple-move-feel.md` Defect 4). `moved` is who moved; it is the card's subject.
 */
export function grappleMoveRider(args: { recomputerId: string; steps: ResolvedStep[]; actorId: string; moved: CombatantId }): ResolutionBroadcastRider | undefined {
  const kind = GRAPPLE_MOVE_RIDER_KINDS[args.recomputerId];
  if (kind === undefined) return undefined;
  return { kind: "resolution", resolution: { steps: args.steps, kind, actorId: args.actorId, about: headlessAbout(args.moved, kind) }, revealedIds: [] };
}

const GRAPPLE_MOVE_RIDER_KINDS: Readonly<Record<string, string>> = { drag: "grapple_drag", grapple: "grapple_end" };

/** Run {@link recomputeAfterMoveInto} once per displaced id. */
function runPostMoveRecomputeForIds(exec: ScratchExecutor, actorId: string, ids: readonly string[]): void {
  for (const id of ids) recomputeAfterMoveInto(exec, actorId, { moved: id });
}

/** Pilot ejection — **MUST run before** {@link spawnWrecksIfOwed} (it reads the dead mech's still-placed `pos`). */
function spawnEjectedPilotsIfOwed(exec: ScratchExecutor, actorId: string): void {
  // A rodeo rider sits outside the dead mech; end the rodeo so the ordinary ejection places it.
  const rodeoEnds = rodeoEndsAtDestruction(exec.state);
  if (rodeoEnds.length > 0) applyTrusted(exec, { type: "apply_resolved", actorId, steps: rodeoEnds, kind: "rodeo_end" }, actorId);
  const { ejections } = resolvePilotEjections(exec.state);
  if (ejections.length === 0) return;
  const commands: EncounterCommand[] = ejections.flatMap((e): EncounterCommand[] => [
    { type: "add_combatant", actorId, combatant: e.pilot },
    ...(e.stampActivated ? [{ type: "stamp_activated" as const, actorId, combatantId: e.pilot.id }] : []),
  ]);
  applyTrusted(exec, { type: "batch", commands }, actorId);
}

/** Wreck conversion — the whole-encounter re-scan sweep. */
function spawnWrecksIfOwed(exec: ScratchExecutor, actorId: string): void {
  const { conversions } = resolveWreckConversions(exec.state);
  if (conversions.length === 0) return;
  const commands: EncounterCommand[] = [
    { type: "apply_resolved", actorId, steps: conversions.map((c) => c.unplaceStep), kind: "wreck_unplace" },
    ...conversions.flatMap((c): EncounterCommand[] => (c.wreck ? [{ type: "add_combatant", actorId, combatant: c.wreck }] : [])),
  ];
  applyTrusted(exec, { type: "batch", commands }, actorId);
}

/**
 * Asset remains (`decisions/0193`) — the re-scan sweep after the wreck sweep: ONE batch that
 * unplaces every destroyed map asset and paints its `remains` tile over its footprint, so one undo
 * restores both. See `../assetRemains.ts`.
 */
function spawnAssetRemainsIfOwed(exec: ScratchExecutor, actorId: string): void {
  const { unplaceSteps, paints } = resolveAssetRemains(exec.state);
  if (unplaceSteps.length === 0) return;
  const commands: EncounterCommand[] = [
    { type: "apply_resolved", actorId, steps: unplaceSteps, kind: "asset_unplace" },
    ...paints.map((p): EncounterCommand => ({ type: "set_terrain", actorId, cell: p.cell, terrain: { tile: p.tile } })),
  ];
  applyTrusted(exec, { type: "batch", commands }, actorId);
}


/**
 * The full destroy-consequence chain: pilot ejection THEN wreck conversion, in that fixed order,
 * then asset remains (a felled tree leaves the board and paints its `deadfall`), then a destroyed
 * drone's system loss, then the
 * source-gone status expiry (a destroyed source's turn-keyed statuses end now), then the Engaged
 * re-sync (a dead hostile no longer engages its neighbours).
 * Exported because it is the ONE primitive every destroy-capable site chains — this file's own
 * generic path, `refereeDeployAction.ts` (Detonate/Disarm Mine's blast can land a kill),
 * `refereeIdentityAction.ts` (Jockey Menu's Shred/Damage cascade can destroy the carrier), and the
 * move/mine-trigger followups.
 */
export function spawnDestructionConsequences(exec: ScratchExecutor, actorId: string): void {
  spawnEjectedPilotsIfOwed(exec, actorId);
  spawnWrecksIfOwed(exec, actorId);
  spawnAssetRemainsIfOwed(exec, actorId);
  destroyDroneSystemsIfOwed(exec, actorId);
  sourceGoneInto(exec, { actorId });
  resyncEngagementInto(exec, actorId);
}

/**
 * The settle pass outside a move: a combatant that dies or leaves the board un-engages its
 * neighbours (and may end a grapple dominance) without anyone moving. Lands as the same
 * `spatial_<id>` entries a move's settle does. Exported for `refereeCommand.ts`'s
 * `remove_combatant` hook.
 */
export function resyncEngagementInto(exec: ScratchExecutor, actorId: string): void {
  settleInto(exec, actorId, actorId);
}

/**
 * A destroyed drone takes its system down with it and leaves the board (`../droneDestruction.ts`):
 * one entry, so one undo restores both.
 */
function destroyDroneSystemsIfOwed(exec: ScratchExecutor, actorId: string): void {
  const steps = resolveDroneSystemLosses(exec.state);
  if (steps.length === 0) return;
  applyTrusted(exec, { type: "apply_resolved", actorId, steps, kind: "drone_destroyed" }, actorId);
}

/**
 * Both audited post-resolution followups a `fireTrigger`-driven hand-chain item can owe against
 * its OWN recorded steps. Exported alongside `rejected`/`spawnDestructionConsequences` because
 * `refereeCommand.ts` needs the exact same chain for its `on_status_lost` handler fire and its
 * `turn_start`/`turn_end`/`round_start`/`round_end` generalized trigger fire — the SAME
 * `fireTrigger`-driven shape this file's own `fireActionTaken` uses.
 */
export function chainDestroyAndMoveFollowups(exec: ScratchExecutor, actorId: string, steps: readonly ResolvedStep[]): void {
  if (stepsLandDestroy(steps)) spawnDestructionConsequences(exec, actorId);
  const movedIds = stepsMovePosition(steps);
  if (movedIds.length > 0) runPostMoveRecomputeForIds(exec, actorId, movedIds);
}

/**
 * The damage notification fire (`decisions/0175`) — `on:"damage"` clauses on each combatant a
 * NON-ATTACK emitter reported in its `Resolved.damaged`, fired through {@link
 * resolveDamageTrigger} against the post-damage state.
 *
 * **Call it right after the emitter's `exec.apply`, and BEFORE the emitter's own
 * `spawnDestructionConsequences`**: a combatant the damage killed took that damage while still
 * standing, and a wreck conversion afterwards would hide it. An id already absent from
 * `exec.state` is skipped. Each fire is its own `apply_resolved` entry (`kind:
 * "damage_trigger"`) and chains {@link chainDestroyAndMoveFollowups}, because fired content can
 * kill or displace. Draws off `ctx.dice` — the chain's own dice source, so a halt later in the
 * same chain replays these draws too.
 *
 * **`dealt` and forced attacks** (`npc-features-fielded-roster` 13.3a). Each report's `final`
 * becomes the event's `dealt` fact, so a settled-damage clause (Aggression's `match.dealt`) fires
 * here. The `forced_attack`s it queues are resolved by {@link resolveForcedAttacks} against the
 * post-fire state and folded into the same entry: their steps, their reaction spends and their cap
 * consumes, then `attack_resolved` for each forced attacker. `ctx.interactive` decides a
 * nested halt (the forced attack's target Braces): interactive, it is thrown as a
 * {@link NeedsDecisionSignal} for the caller's own whole-chain re-run (`refereeMove`,
 * `refereeGmDamage`); otherwise the nested attack takes its non-interactive defaults (an unanswered
 * reaction offer is declined).
 *
 * **Wired at** every non-attack damage emitter — the list, and what is excluded and why, is
 * `resolveDamageTrigger`'s site audit. Never at an attack site — see its caller contract.
 */
export function fireDamageTakenInto(
  exec: ScratchExecutor,
  ctx: { actorId: string; dice: DiceSource; overlay: LoadedOverlay; choices?: Record<string, string>; interactive?: boolean; loop?: EventLoop },
  damaged: readonly DamageReport[] | undefined,
): void {
  // A status its carrier's taking damage ends (Active Camouflage's Invisible) comes off first.
  endOnDamageInto(exec, ctx, damaged ?? []);
  for (const report of damaged ?? []) {
    if (exec.state.combatants[report.id] === undefined) continue;
    const input = diceInputSource(ctx.dice, ctx.loop);
    const fired = resolveDamageTrigger({ state: exec.state, damagedId: report.id, input, overlay: ctx.overlay, actorId: ctx.actorId, dealt: report.final > 0, dtype: report.dtype, ...(ctx.choices !== undefined ? { intercedeAnswers: ctx.choices } : {}), ...(ctx.interactive !== undefined ? { interactive: ctx.interactive } : {}) });
    if (fired === null) continue;
    let resolved: Resolved = fired;
    if (fired.forcedAttacks !== undefined && fired.forcedAttacks.length > 0) {
      const folded = resolveForcedAttacks({
        state: exec.state,
        result: fired,
        input,
        actorId: ctx.actorId,
        effects: { overlay: ctx.overlay, interactive: ctx.interactive === true },
        choices: ctx.choices,
      });
      if (isNeedsDecision(folded)) {
        throw new NeedsDecisionSignal(folded.promptId, folded.prompt, { options: folded.options, who: folded.who, ...(folded.answerKind !== undefined ? { answerKind: folded.answerKind } : {}) });
      }
      resolved = folded;
    }
    const steps = resolved.resourceCapConsumeSteps !== undefined ? [...resolved.command.steps, ...resolved.resourceCapConsumeSteps] : resolved.command.steps;
    const spends = reactionSpendCommands(ctx.actorId, resolved.reactionSpentBy);
    const applied: EncounterCommand = { ...resolved.command, steps };
    const command: EncounterCommand = spends.length > 0 ? { type: "batch", commands: [applied, ...spends] } : applied;
    // The fire's warnings (an advisory, a forced attack's own lines) ride a resolution rider so
    // they reach the table.
    unwrap(exec.apply(command, ctx.actorId, warningRider({ kind: "damage_trigger", steps: resolved.command.steps, actorId: ctx.actorId, actor: report.id, warnings: resolved.warnings })));
    endOnDamageInto(exec, ctx, damageWitnessReports(resolved.command.steps));
    for (const attacker of forcedAttackersIn(resolved.command.steps)) raiseAttackResolved(exec, { actorId: ctx.actorId, attacker, loop: input.loop ?? createEventLoop({ dice: ctx.dice }) });
    chainDestroyAndMoveFollowups(exec, ctx.actorId, resolved.command.steps);
  }
}

/**
 * The reactions an activation's own walk provokes (Impale, Core p.297: "Cataphracts still provoke
 * reactions when they move"; `MoveSelfTowardTargetAtom.provokesReactions`,
 * `landing-zone-full-automation` Q7). The room's move checkpoint gathers against the walk's origin
 * and endpoint on the PRE-resolution state, as a `request_move` from that cell would, with the
 * intent's recorded answers. An unanswered offer halts the whole activation (`needs_decision`,
 * nothing commits) with the watcher as decider; the answer re-runs it against the post-shot state.
 * Once every offer is answered, the recorded declines are owed as `record_move_reaction_decline`s,
 * so a declined watcher is not asked again this turn. A walk that went nowhere provokes nothing.
 */
function provokedReactions(
  state: EncounterState,
  args: { movePath: MovePath; choices: Record<string, string> | undefined; loop: EventLoop; ctx: RefereeIntentCtx },
): { halt: NeedsDecision } | { declines: RecordMoveReactionDeclineCommand[] } {
  const { movePath, choices, loop, ctx } = args;
  const dice = loop.dice;
  const from = movePath.path[0];
  const to = movePath.path.at(-1);
  if (from === undefined || to === undefined || worldFor(state).equals(from, to)) return { declines: [] };
  const checkpoint: MoveCheckpointContext = {
    state,
    declaredMove: { moverId: movePath.mover, from, to, ...(movePath.voluntary === false ? { voluntary: false as const } : {}) },
    choices,
    interactive: true,
    dice,
    overlay: ctx.overlay,
  };
  try {
    raiseMoveDeclared(loop, { checkpoint, consumers: ctx.checkpointConsumers });
  } catch (err) {
    if (!(err instanceof NeedsDecisionSignal)) throw err;
    return {
      halt: {
        kind: "needs_decision",
        who: err.who ?? movePath.mover,
        promptId: err.promptId,
        prompt: err.prompt,
        options: err.options,
        ...(err.title !== undefined ? { title: err.title } : {}),
      },
    };
  }
  return { declines: declineCommandsFor(checkpoint, ctx.checkpointConsumers, ctx.actorId) };
}

/** Every attacker a `to_hit` step in `steps` names, first-seen order — the forced attackers of a folded fire. */
function forcedAttackersIn(steps: readonly ResolvedStep[]): string[] {
  return [...new Set(steps.flatMap((s) => (s.op === "to_hit" ? [s.attacker] : [])))];
}

/**
 * `on_action_taken` — fires for the taken-action identity `refereeIntent`'s own economy fold just
 * stamped (never re-derived). Draws dice directly off `ctx.dice`, **NOT** the `recordingDice`
 * wrapper this file builds at `resolveIntent` — and the reason has changed shape, so read both
 * halves before deciding whether your own new case belongs on the wrapper or off it.
 *
 * **The original reasoning, which still holds here.** The wrapper exists to support a
 * replay-then-fresh `needs_decision` contract: the halted resolution's log is echoed back on the
 * intent and replayed on the re-submit. A trigger fire that CANNOT halt has nothing to replay, so
 * recording it would only pad a log nobody reads. `on_action_taken` fires strictly after the
 * primary `resolve()` has completed — it is downstream of the one halt point on this spine.
 *
 * **What changed: the rule is "any chain that can halt", not "the primary resolve".** When this
 * comment was written, the set of chains that could pause was exactly one, so "the primary
 * `resolve()`'s wrapper" and "every halt-capable chain" named the same thing and the narrower
 * phrasing was harmless. `decisions/0164` Decision 1 grew that set: the hooked-command chains
 * (`refereeCommand`'s `driveEndTurn` and the move family) and `refereeMove`'s post-move followup
 * chain each gained a halt point, so each now runs on its own single recording source built at
 * its entry point. This site is not an exception to that rule — it is outside it, because nothing
 * it fires can halt. If that ever becomes false, this fire moves onto the wrapper too, and the
 * test to write first is the nesting pin (`./moveChainRecordingDice.test.ts`): one instance per
 * chain, never a second over the same recorded log.
 */
function fireActionTaken(exec: ScratchExecutor, ctx: RefereeIntentCtx, args: { actorId: string; carrierId: string; actionId: string; verb?: EarmarkVerb; primaryTarget?: string; choices?: Record<string, string> }): void {
  const { actorId, carrierId, actionId, verb, primaryTarget, choices } = args;
  const resolved = resolveActionTakenTrigger({
    state: exec.state,
    carrierId,
    actionId,
    ...(verb !== undefined ? { verb } : {}),
    ...(primaryTarget !== undefined ? { primaryTarget } : {}),
    input: diceInputSource(ctx.dice, ctx.intentLoop?.loop),
    overlay: ctx.overlay,
    actorId,
    choices,
  });
  if (resolved === null) return;
  const command =
    resolved.resourceCapConsumeSteps !== undefined && resolved.resourceCapConsumeSteps.length > 0
      ? { ...resolved.command, steps: [...resolved.command.steps, ...resolved.resourceCapConsumeSteps] }
      : resolved.command;
  // The fired clause's advisories ride the entry (see `refereeCommand`'s `fireActionTakenInto`).
  unwrap(exec.apply(command, actorId, warningRider({ kind: "action_taken_trigger", steps: resolved.command.steps, actorId, actor: carrierId, warnings: resolved.warnings })));
  chainDestroyAndMoveFollowups(exec, actorId, resolved.command.steps);
}

/**
 * The room-side broadcast rider `refereeIntent` stashes on the PRIMARY resolution's plan entry —
 * opaque to the referee itself, meaningful only to the room's own per-entry broadcast walk.
 * `revealedIds` are the ids the post-resolve auto-reveal sweep cleared concealment on; the room
 * unions them into its own `revealed` accumulator (already seeded by the pre-dice reveal, run
 * before this call) before calling `broadcastResolution`. Every OTHER plan entry carries no rider
 * — the room's default per-entry broadcast is a plain `broadcastChange`.
 */
export type ResolutionBroadcastRider = {
  kind: "resolution";
  resolution: { steps: ResolvedStep[]; kind?: string; actorId: string; promptIds?: string[]; areaOrigin?: Cell; warnings?: string[]; about?: ResolutionAbout };
  revealedIds: string[];
};

/**
 * The `about` every rider without an attack roll records (`narration-ledger-retirement` 2.1): the
 * acting combatant, and the resolution kind as its action, so the narrator can say who did what
 * off the record alone. `item` is the drone, mine or system involved, when there is one.
 */
export function headlessAbout(actor: CombatantId, kind: string | undefined, item?: ResolutionAbout["item"]): ResolutionAbout {
  return { actor, ...(kind !== undefined ? { action: kind } : {}), ...(item !== undefined ? { item } : {}) };
}

/**
 * The rider a non-primary fire rides so its `warnings` reach the table: the CLI's `! …` lines and
 * the COMMS card. It also rides a fire that folded a chain `link` (Aggression's swing after GM
 * damage, `decisions/0210`), which the table must see even with no warning to show. `undefined`
 * otherwise, so a clean fire keeps its plain change broadcast. `actor` is the combatant whose
 * trigger or sweep fired; it becomes `about`. It is absent only for a round-edge fire over several
 * carriers, where no one combatant acted.
 */
export function warningRider(args: { kind: string; steps: ResolvedStep[]; actorId: string; actor: CombatantId | undefined; warnings: readonly string[] }): ResolutionBroadcastRider | undefined {
  const { kind, steps, actorId, actor, warnings } = args;
  if (warnings.length === 0 && !steps.some((s) => s.op === "link")) return undefined;
  return { kind: "resolution", resolution: { steps, kind, actorId, warnings: [...warnings], ...(actor !== undefined ? { about: headlessAbout(actor, kind) } : {}) }, revealedIds: [] };
}

/** Narrow an opaque `RefereePlanEntry.broadcastRider` to a {@link ResolutionBroadcastRider}. */
export function isResolutionBroadcastRider(rider: unknown): rider is ResolutionBroadcastRider {
  return typeof rider === "object" && rider !== null && (rider as { kind?: unknown }).kind === "resolution";
}

/**
 * The extra targets of a multi-target attack (`RequestAttack.alsoTargets`): a unit-target weapon
 * attack whose weapon entry carries `attacksTargets`, at most that many targets in all, each a
 * distinct placed combatant other than the attacker, in range and line of sight. `undefined` when
 * legal, else the refusal and its category.
 */
function checkAlsoTargets(args: { state: EncounterState; intent: RequestAttack; overlay: LoadedOverlay; rangeBonus: { rangeBonus?: number } }): { error: string; category: "illegal" | "range" } | undefined {
  const { state, intent, overlay, rangeBonus } = args;
  const also = intent.alsoTargets ?? [];
  const attacker = state.combatants[intent.attacker];
  if (attacker === undefined || intent.tech || intent.target.kind !== "unit") return { error: "attack: extra targets need a weapon attack on a unit target", category: "illegal" };
  const weapon = selectWeapon(attacker, intent.weapon, undefined);
  const cap = attacksTargets(overlay, weapon.id);
  if (cap === undefined) return { error: `attack: ${weapon.name} attacks one target at a time`, category: "illegal" };
  const ids = [intent.target.id, ...also];
  if (ids.length > cap) return { error: `attack: ${weapon.name} attacks at most ${cap} targets at a time`, category: "illegal" };
  if (new Set(ids).size !== ids.length) return { error: "attack: each target may be named once", category: "illegal" };
  for (const id of also) {
    if (id === intent.attacker) return { error: "attack: an attacker can't target itself", category: "illegal" };
    if (state.combatants[id] === undefined) return { error: `attack: no combatant "${id}"`, category: "illegal" };
    const reach = checkAttackRange({ state, attacker: intent.attacker, target: id, weapon: intent.weapon, profile: intent.profile, ...rangeBonus });
    if (!reach.ok) return { error: reach.error, category: "range" };
    const los = checkAttackLoS({ state, attacker: intent.attacker, target: id, weapon: intent.weapon, profile: intent.profile });
    if (!los.ok) return { error: los.error, category: "range" };
  }
  return undefined;
}

/**
 * The `about.weapon` breadcrumb (`resolution-narration` 1.1) — reads the frozen
 * {@link WeaponProfile} `selectWeapon` (`../resolve`) resolves off the acting combatant's OWN
 * loadout (`base.weapons`, snapshotted at spawn): zero compendium lookup, the same lookup
 * `resolve()`'s own attack spine performs. `undefined` for any intent with no weapon slot at all
 * (`request_activate`/`request_stabilize`/`request_overcharge`) or when the acting combatant
 * can't be found (defensive; never throws). **Known gap, accepted for Phase 1**: this doesn't
 * account for an overlay-driven profile auto-select (`firingProfile`'s `selectProfileByState`
 * branch, Apocalypse Die) — a multi-profile weapon whose active profile wasn't named on the
 * intent may narrate under its default profile's name instead. Phase 1 is the single-target
 * weapon attack; revisit if a multi-profile fixture narrates wrong.
 *
 * **Never redacted here** — this always records the true weapon, full stop. The per-viewer
 * "would a player see this NPC's weapon?" redaction is a SEPARATE, later concern (the disclosure
 * rule at `EncounterRoom.broadcastResolution`, `backend/src/encounterRoomCore.ts`) — recording
 * the true fact here and stripping it once, downstream, before either broadcast sink, is what
 * keeps this record viewer-agnostic.
 *
 * **A TECH ATTACK records NO weapon at all** (`resolution-narration` 2.3), and the absence is the
 * correct record rather than a gap. A tech attack's `intent.weapon` is an ACTION id (`act_invade`,
 * or a composite system-granted id like `ms_markerlight:0`), which {@link selectWeapon} cannot
 * find in `base.weapons` — so it fell through to whatever that loadout's first entry was, or to
 * `FALLBACK_WEAPON` ("Unarmed strike", id `__fallback__`) for a weaponless attacker. Measured off
 * a real `cli/proofs/tech-invade.test.ts` INVADE against a real backend: `about.weapon` came back
 * `{ name: "Unarmed strike", id: "__fallback__" }`, which the narrator would have rendered as
 * "fires the Unarmed strike at Defender" for an attack that fired no weapon. A wrong fact on the
 * record is worse than an absent one (`decisions/0177`), and the narrator already degrades
 * honestly when `about.weapon` is missing, so the fix is here rather than at render time. A tech
 * attack's own identity stays readable off `about.action` (`"quick_tech"`) and the `to_hit` step's
 * `vs: "edef"`; the specific tech OPTION (Invade's FRAGMENT SIGNAL, say) is recorded separately,
 * on `about.techOption` ({@link aboutTechOptionFor}).
 *
 * `melee` (`comms-attack-reaction-cards` 1.2) is the same `isRangedWeaponAttack` read the resolver's
 * ranged/melee gates make, so an either-mode weapon records the mode this attack resolved in.
 */
function aboutWeaponFor(state: EncounterState, intent: GenericResolutionIntent, actorCombatantId: string): NonNullable<ResolutionAbout["weapon"]> | undefined {
  if (intent.type !== "request_attack" && intent.type !== "request_barrage") return undefined;
  if (intent.type !== "request_barrage" && intent.tech === true) return undefined;
  const attacker = state.combatants[actorCombatantId];
  if (attacker === undefined) return undefined;
  const profile = selectWeapon(attacker, intent.weapon, intent.profile);
  // `about.weapon.area` (`resolution-narration` 2.1) — the SAME `areaShapeOf` read
  // `resolveAreaAttack` performs off the SAME frozen profile, recorded so the narrator can
  // name the shape ("fires the Mortar (Blast 1)") off the durable feed alone. `undefined` for
  // an ordinary single-target weapon, which is exactly when the story line omits the clause.
  const area = areaShapeOf(profile.range ?? []);
  const melee = !isRangedWeaponAttack({ state, attacker, intent, weapon: profile });
  return { name: profile.name, id: profile.id, ...(area !== undefined ? { area } : {}), ...(melee ? { melee: true as const } : {}) };
}

/**
 * The `about.auxRider` breadcrumb: the earlier Aux a Main's shot paired with, named off the frozen
 * loadout. `{}` when the weapon can't be found (defensive), so the pairing is still recorded.
 */
function aboutAuxRiderFor(state: EncounterState, actorCombatantId: string, auxKey: string): NonNullable<ResolutionAbout["auxRider"]> {
  const w = (state.combatants[actorCombatantId]?.base.weapons ?? []).find((x) => weaponInstanceKey(x) === auxKey);
  return w === undefined ? {} : { weapon: { name: w.name, id: w.id } };
}

/**
 * The routed intent one Full Tech pick resolves as: a built-in option's (`quickTechIntent`:
 * Invade's tech attack, or Scan/Lock On/Bolster's activation), else the carried Quick Tech
 * action's own `request_activate` (Crush Targeting), exactly as a standalone use of it.
 */
/**
 * The attacker of an intent that makes an attack: a weapon or tech `request_attack` (Overwatch and
 * Invade included, forced or not), a Barrage shot, or a Full Tech's attack. `undefined` for an
 * activation (a thrown grenade is not an attack) and the self actions.
 */
function attackerOfAttack(intent: GenericResolutionIntent): string | undefined {
  if (intent.type === "request_attack" || intent.type === "request_barrage") return intent.attacker;
  if (intent.type === "request_full_tech") {
    const sub = fullTechSubIntent(intent);
    return sub.type === "request_attack" ? sub.attacker : undefined;
  }
  return undefined;
}

/** The `action_declared` event an intent opens with: who declares what, against whom. */
function declarationOf(intent: GenericResolutionIntent, facts: IntentFacts): ActionDeclaredEvent {
  const targets = (() => {
    switch (intent.type) {
      case "request_attack":
      case "request_barrage":
        return [...(intent.target.kind === "unit" ? [intent.target.id] : []), ...(intent.alsoTargets ?? [])];
      case "request_activate":
        return intent.target !== undefined ? [intent.target] : [];
      case "request_full_tech":
        return [intent.target];
      default:
        return [];
    }
  })();
  const own = intent.type === "request_attack" || intent.type === "request_barrage" ? intent.weapon : intent.type === "request_activate" ? intent.action : intent.type === "request_full_tech" ? intent.option : undefined;
  return { kind: "action_declared", actor: facts.cost.actor, action: facts.actionId ?? own ?? intent.type, isReaction: facts.cost.reaction === true, targets };
}

function fullTechSubIntent(intent: RequestFullTech): RequestActivate | RequestAttack {
  const choices = intent.choices !== undefined && Object.keys(intent.choices).length > 0 ? { choices: intent.choices } : {};
  if (quickTechOption(intent.option) !== undefined) {
    return quickTechIntent(intent.option, { actor: intent.actor, target: intent.target, accuracy: intent.accuracy ?? 0, ...choices });
  }
  return { type: "request_activate", actor: intent.actor, action: intent.option, target: intent.target, ...choices };
}

/**
 * The `about.techOption` breadcrumb (`comms-attack-reaction-cards` 1.3) — which option a TECH
 * attack fired, when the record gives a real id and name.
 *
 * **The one source that qualifies is an answered `choose` on the tech action's own overlay entry.**
 * INVADE (`act_invade`, `effects/seedEntries.ts`) is authored as an on-hit `triggered` clause whose
 * `choose` (prompt `invade_option`) is the invasion menu; its answer rides the intent's `choices`
 * keyed by that prompt (`--choice invade_option=fragment_signal`), and the option's authored label
 * leads with its display name (`"Fragment Signal — the target becomes …"`). So the id is the
 * answered option id and the name is the label's text before its ` — `. An option the attacker
 * contributes (`extend: "invade_options"`) is looked up among its gathered invade options. A Full
 * Tech pick routes through the same Quick Tech table, so its Invade records the same way. `action` is the Quick
 * Tech menu's RAW name for the action that fired (`"Invade"`), so a card can read
 * `Invade · Fragment Signal` without a compendium.
 *
 * Recorded nothing, deliberately, when:
 *  - **the attack missed.** The menu runs only on a hit; a pre-answered choice on a miss (the CLI
 *    pre-answers) names an option that never took effect.
 *  - **the tech attack is system-granted** (`ms_markerlight:0`). The overlay lookup is bare-exact,
 *    so a composite id finds no entry here (the same miss `gatherHandlers` makes by design), and
 *    the ACTION's display name is on nothing the compendium-free engine holds — the frozen
 *    `equippedSystems` carries the SYSTEM's name, which is a different claim.
 *  - **the option carries no label.** A bare id is not a name, and titling it would be the
 *    render-time guess `decisions/0177` forbids, moved one layer down.
 */
function aboutTechOptionFor(args: {
  state: EncounterState;
  intent: GenericResolutionIntent;
  overlay: LoadedOverlay;
  steps: readonly ResolvedStep[];
}): NonNullable<ResolutionAbout["techOption"]> | undefined {
  const { state, intent, overlay, steps } = args;
  const techWeapon =
    intent.type === "request_attack" && intent.tech === true
      ? intent.weapon
      : intent.type === "request_full_tech"
        ? techAttackWeaponOf(quickTechOption(intent.option)?.route)
        : undefined;
  if (techWeapon === undefined) return undefined;
  if (!steps.some((s) => s.op === "to_hit" && s.result !== "miss")) return undefined;
  const choices = ("choices" in intent ? intent.choices : undefined) ?? {};
  const entry = overlay[techWeapon];
  if (entry === undefined) return undefined;
  // The Quick Tech menu's own RAW name for the action ("Invade") — a pure rules table, not the
  // compendium; absent for anything the menu does not route.
  const action = QUICK_TECH_OPTIONS.find((o) => techAttackWeaponOf(o.route) === techWeapon)?.name;
  for (const effect of entry.effects) {
    if (!("effect" in effect) || !Array.isArray(effect.effect)) continue;
    for (const choose of chooseAtomsIn(effect.effect)) {
      const answer = choices[choose.prompt];
      const attackerId = "attacker" in intent ? intent.attacker : "actor" in intent ? intent.actor : undefined;
      const extended = choose.extend === "invade_options" && attackerId !== undefined ? gatherInvadeOptions(state, attackerId, overlay) : [];
      const option = answer === undefined ? undefined : [...choose.options, ...extended].find((o) => o.id === answer);
      const name = option?.label?.split(" — ")[0]?.trim();
      if (option !== undefined && name !== undefined && name !== "") return { id: option.id, name, ...(action !== undefined ? { action } : {}) };
    }
  }
  return undefined;
}

/** INVADE's menu prompt (`act_invade`'s `choose`), the key a pre-answered option rides in `choices`. */
const INVADE_PROMPT = "invade_option";

/**
 * Why a pre-answered INVADE option is refused before the dice (`ll1-invade-options` I2), or
 * `undefined` when it may be taken: it must be one of `act_invade`'s own options (Fragment Signal)
 * or an invade option the attacker carries whose `when` holds against this target. A bare Invade
 * with no answer passes and keeps its post-hit prompt.
 */
function invadeAnswerRefusal(args: { state: EncounterState; attackerId: string; targetId: string; choices: Record<string, string> | undefined; overlay: LoadedOverlay }): string | undefined {
  const { state, attackerId, targetId, choices, overlay } = args;
  const answer = choices?.[INVADE_PROMPT];
  if (answer === undefined) return undefined;
  const own = (overlay.act_invade?.effects ?? []).flatMap((e) => ("effect" in e && Array.isArray(e.effect) ? chooseAtomsIn(e.effect) : []));
  if (own.some((c) => c.prompt === INVADE_PROMPT && c.options.some((o) => o.id === answer))) return undefined;
  if (offeredInvadeOptions({ state, attackerId, targetId, overlay }).some((o) => o.id === answer)) return undefined;
  const attacker = state.combatants[attackerId]?.name ?? attackerId;
  const target = state.combatants[targetId]?.name ?? targetId;
  return gatherInvadeOptions(state, attackerId, overlay).some((o) => o.id === answer)
    ? `invade: ${attacker} can't take "${answer}" against ${target}`
    : `invade: ${attacker} has no invade option "${answer}"`;
}

/**
 * The tech-attack weapon id a Quick Tech route fires, or `undefined` for an `activate` route.
 * Narrowed by the `weapon` field rather than the route's discriminant.
 */
function techAttackWeaponOf(route: QuickTechRoute | undefined): string | undefined {
  return route !== undefined && "weapon" in route ? route.weapon : undefined;
}

/** Every `choose` atom in an authored effect list, depth-first through `choose` options and `if` branches. */
function chooseAtomsIn(atoms: readonly EffectAtom[]): ChooseAtom[] {
  const out: ChooseAtom[] = [];
  for (const atom of atoms) {
    if (atom.op === "choose") {
      out.push(atom);
      for (const o of atom.options) out.push(...chooseAtomsIn(o.effect));
    } else if (atom.op === "if") {
      out.push(...chooseAtomsIn(atom.then), ...chooseAtomsIn(atom.else ?? []));
    }
  }
  return out;
}

/**
 * The `about.shot` breadcrumb (`comms-attack-reaction-cards` 1.1) — which shot of a Barrage or a
 * rider-bearing Skirmish this resolution was, read off the `open_attack` sibling this function
 * already folded (so "does this shot belong to a lock" has exactly one answer, the one the reducer
 * applies) and the PRE-shot lock's fired list: a continuation is shot `weapons.length + 1`, an
 * opener is shot 1. A Skirmish that opened no lock, and Full Tech (out of the card's scope), get
 * nothing.
 */
function aboutShotFor(args: { siblings: readonly EncounterCommand[]; midAttack: boolean; lock: AttackLock | null }): ResolutionAbout["shot"] {
  const { siblings, midAttack, lock } = args;
  const opened = siblings.find((c): c is Extract<EncounterCommand, { type: "open_attack" }> => c.type === "open_attack");
  if (opened === undefined || (opened.action !== "barrage" && opened.action !== "skirmish")) return undefined;
  return { action: opened.action, index: midAttack && lock !== null ? lock.weapons.length + 1 : 1 };
}

/**
 * The `about.targets` breadcrumb — the DECLARED target ids off the intent itself, never the
 * resolved/actual targets a `ResolvedStep` records (an area attack's declared aim and its
 * caught targets can differ; that's the steps' job, not this breadcrumb's). `undefined` for a
 * cell-targeted area attack (area-effects v1 — no combatant was declared) and for the
 * self-only intents (Stabilize, Overcharge).
 */
function aboutTargetsFor(intent: GenericResolutionIntent): CombatantId[] | undefined {
  if (intent.type === "request_attack" || intent.type === "request_barrage") {
    return intent.target.kind === "unit" ? [intent.target.id] : undefined;
  }
  if (intent.type === "request_full_tech") return [intent.target];
  if (intent.type === "request_activate") return intent.target !== undefined ? [intent.target] : undefined;
  return undefined;
}

/**
 * The frozen {@link import("@lancer-vtt/domain").Affordance} record a `request_activate`'s wire id
 * names — this file's two `action_use` raises (the live path and the release path) read their
 * rate, pool and ledger key off it, so both name the SAME slots
 * `checkGrantedActionLegality`'s reads gate on (`decisions/0176` ruling 3).
 *
 * An `ambiguous` verdict (a bare granted id naming several of this carrier's affordances) is
 * rejected pre-dice by `checkGrantedActionLegality` and cannot reach a fold; it degrades to a
 * capless record on the id as named rather than guessing which affordance to bill.
 */
function affordanceFor(args: {
  state: EncounterState;
  intent: { actor: string; action: string };
  overlay: LoadedOverlay;
}): Affordance {
  const { state, intent, overlay } = args;
  const resolved = resolveAffordance({ state, actorId: intent.actor, wireId: intent.action, overlay });
  return resolved.kind === "affordance"
    ? resolved.affordance
    : { key: intent.action, source: { kind: "effect", entityId: intent.action } };
}

/**
 * One `action_use` cost per economy fact the record carries (`decisions/0202`): its round or scene
 * rate at `key` on that grain's ledger, and its Limited pool at `pool ?? key` on the per-affordance
 * ledger. These are the slots `checkGrantedActionLegality` gates on. A `turn` rate rides the
 * once-per-turn `actionUses` spend instead, so it raises nothing here.
 */
function affordanceUses(args: { affordance: Affordance; combatantId: CombatantId }): ActionUseEvent[] {
  const { affordance, combatantId } = args;
  const uses: ActionUseEvent[] = [];
  const { cap, limited } = affordance;
  if (cap?.grain === "round" || cap?.grain === "scene") {
    uses.push({ kind: "action_use", carrier: combatantId, key: affordance.key, grain: cap.grain, max: cap.max });
  }
  if (limited) {
    uses.push({ kind: "action_use", carrier: combatantId, key: affordance.key, grain: "scene", max: limited.max, ...(limited.pool !== undefined ? { pool: limited.pool } : {}) });
  }
  return uses;
}

/**
 * The `about.item` breadcrumb (`narration-ledger-retirement` 2.2): the system, pilot gear or NPC
 * feature a `request_activate` fired, named off the granted affordance's frozen `name` (the engine
 * holds no compendium). Absent for every other intent, and for the effect class (frame traits,
 * talents, core actions), whose records freeze no name.
 */
function aboutItemFor(state: EncounterState, intent: GenericResolutionIntent, overlay: LoadedOverlay): ResolutionAbout["item"] {
  if (intent.type !== "request_activate") return undefined;
  const affordance = affordanceFor({ state, intent, overlay });
  if (affordance.source.kind !== "granted" || affordance.name === undefined) return undefined;
  return { name: affordance.name, id: affordance.source.itemId };
}

/**
 * The referee for the room's **generic `resolve()`/`resolveActivate()` intent flow** — see this
 * file's header for the full boundary. Drives a scratch history over a copy of `state`, running
 * the shared gate ladder → the `resolve()`-family invocation over `ctx.dice` → the economy sibling
 * batch fold → the eager post-apply followup chain, and returns the accumulated
 * {@link RefereePlan}. Every gate reject returns `{entries: [], rejection}`; a mid-resolve
 * `choose`/Brace returns `{entries: [], needsDecision}` (the whole-resolution re-run mode — no
 * entries); a resolution whose action gated to zero steps returns `{entries: []}` with neither, a
 * legitimate no-op.
 */
/** The refusal a `cannotAttack` carrier (the rig's Unarmed) gets for any attack; `source` is the carrying entity id. */
/**
 * The suffix an Overwatch refusal carries when the target carries the attacker's Covering Fire
 * Mark: the shot fell to Overwatch only because the weapon is not Heavy ranged, so name that.
 */
function notCoveringFireNote(state: EncounterState, intent: RequestAttack): string {
  if (intent.target.kind !== "unit" || !hasCoveringFireMarkFrom(state, { attacker: intent.attacker, target: intent.target.id })) return "";
  const atk = state.combatants[intent.attacker];
  if (!atk) return "";
  const w = selectWeapon(atk, intent.weapon, intent.profile);
  return ` — and it can't be the Covering Fire shot: ${w.name} is not a Heavy ranged weapon (Core p.97)`;
}

function cannotAttackRefusal(state: EncounterState, args: { actorId: string; source: string }): string {
  const name = state.combatants[args.actorId]?.name ?? args.actorId;
  return `attack: ${name} cannot make attacks (${args.source})`;
}

/** {@link adjudicateIntent} with the track observer's `track_damage`/`heat` fires after it (`trackObserver.ts`). */
export function refereeIntent(state: EncounterState, intent: GenericResolutionIntent, ctx: RefereeIntentCtx): RefereePlan {
  const withLoop = { ...ctx, intentLoop: ctx.intentLoop ?? createIntentLoop(intent, ctx.dice) };
  return askPendingReactionMove(observeTrackChanges({ before: state, plan: adjudicateIntent(state, intent, withLoop), ctx: { ...withLoop, loop: withLoop.intentLoop.loop } }));
}

/**
 * The gameplay door: legality, then resolution. It takes no skip parameter; the only way past
 * {@link checkIntentLegality} is the GM override's own entry point (`./refereeForce.ts`), which
 * calls {@link resolveIntentPlan} with the forced spend policy after a structural check.
 */
function adjudicateIntent(state: EncounterState, intent: GenericResolutionIntent, ctx: RefereeIntentCtx): RefereePlan {
  const facts = intentFacts(state, intent, ctx.overlay);
  return checkIntentLegality({ state, intent, ctx, facts }) ?? resolveIntentPlan({ state, intent, ctx, facts, policy: RULES_SPEND });
}

/** The pre-derived turn, lock and economy facts both halves read. */
export type IntentFacts = {
  turn: EncounterState["activation"]["turn"];
  lock: AttackLock | null;
  midAttack: boolean;
  cost: ReturnType<typeof intentCost>;
  actionId: string | null;
};

/** How a resolution pays for itself (`events/gameEvents.ts`); a forced one also never opens an attack lock. */
export { spendsThisTurn, type SpendPolicy } from "../events/gameEvents";

function intentFacts(state: EncounterState, intent: GenericResolutionIntent, overlay: LoadedOverlay): IntentFacts {
  // Re-derive the mid-attack-lock / cost / action-id facts the room's own shared gate ladder
  // (still room-side, in front of this call) already computed off the SAME pre-apply state —
  // cheap, pure, and keeps this function's ctx bounded (no threading of room-computed values).
  const turn = state.activation.turn;
  const lock = activeAttack(state.activation);
  const midAttack = isAttackLockContinuation({ state, intent, lock });
  // Executioner's Backswing Cut cash-in: the room's `stampBackswingFollowup` already matched the
  // reservation and stamped `intent.consumeReservation` (the name to consume) +
  // `damageScale:"half"` server-side.
  // Its presence is the signal — a FREE follow-up (zero cost, no once-per-turn use), byte-for-byte
  // the same zeroing `midAttack` gets, and it folds a `remove_status` consume sibling below.
  const followupReservation = intent.type === "request_attack" && intent.consumeReservation !== undefined;
  const cost =
    midAttack || followupReservation
      ? {
          actor: intent.type === "request_attack" || intent.type === "request_barrage" ? intent.attacker : intent.actor,
          points: 0,
          reaction: false,
          label: followupReservation ? "backswing (follow-up)" : intent.type === "request_barrage" ? "barrage (continuation)" : intent.type === "request_full_tech" ? "full tech (continuation)" : "skirmish (rider)",
        }
      : intentCost(intent, overlay, state);
  // **`overlay` is REQUIRED here**: every non-Protocol granted `request_activate` derives a
  // ledger id unconditionally, so the Protocol carve-out check inside `actionIdForIntent` runs
  // against
  // this room's REAL loaded overlay, not the small engine-test `SEED_OVERLAY` default — an
  // omitted overlay here would misjudge a real Protocol action as non-Protocol.
  const actionId = actionIdForIntent(intent, { midAttack, followupReservation, state, overlay });
  return { turn, lock, midAttack, cost, actionId };
}

/**
 * **The legality half** of the generic family: every rule-bearing gate this referee rings, in
 * order, returning the first refusal as a rejected plan (`undefined` when the intent is legal).
 * The room's shared gate ladder (budget, ledger, status, lock) runs in front of it.
 */
function checkIntentLegality(args: { state: EncounterState; intent: GenericResolutionIntent; ctx: RefereeIntentCtx; facts: IntentFacts }): RefereePlan | undefined {
  const { state, intent, ctx, facts } = args;
  const { releaseCtx, overlay } = ctx;
  const { lock, midAttack, actionId } = facts;

  // ── Tech-attack legality wall, TECH direction — deliberately hoisted ABOVE the
  // reaction/non-reaction split and gated only on `intent.tech`, NOT `!intent.reaction`.
  // `RequestAttack` carries `reaction` and `tech` as INDEPENDENT booleans (the CLI's
  // `--overwatch --tech` wires both through with no mutual-exclusion check), so inside the
  // non-reaction block `reaction: true` would skip it entirely and route into the Overwatch gates
  // below — whose `checkAttackWeapon` loadout check has a deliberate weaponless-mech carve-out
  // (`weapons.length > 0 &&`, `economy.ts`). A WEAPONLESS attacker forging
  // `{reaction:true, tech:true, weapon:<real shipped tech-attack-shaped id>}` then sails through
  // both walls and inherits that entry's on-hit clauses. No legitimate `reaction:true &&
  // tech:true` path exists anywhere in the codebase (reactions are the Overwatch weapon-attack
  // path; tech attacks are never reactions), so the unconditional check costs no legal case.
  if (intent.type === "request_attack" && intent.tech) {
    const techWeapon = intent.weapon ?? "act_invade";
    if (!legalTechAttackWeaponIds(state, intent.attacker).has(techWeapon)) {
      return rejected(state, `tech attack: "${techWeapon}" is not a legal tech-attack weapon id`, "illegal");
    }
    const techCharge = checkTechAttackRecharge({ state, attacker: intent.attacker, weapon: intent.weapon });
    if (!techCharge.ok) return rejected(state, techCharge.error, "illegal");
  }

  // ── Can this attacker make attacks at all? (the rig's Unarmed, `cannotAttack`) ──
  // Every attack-shaped intent — a weapon shot, a tech attack, Ram/Grapple/Improvised Attack, an
  // Overwatch reaction, a Barrage shot. A GM `gm_force` never enters this legality half.
  if (intent.type === "request_attack" || intent.type === "request_barrage") {
    const source = cannotMakeAttacks({ state, actorId: intent.attacker, overlay });
    if (source !== undefined) return rejected(state, cannotAttackRefusal(state, { actorId: intent.attacker, source }), "illegal");
  }
  // The attacker's held stance (Siege Stabilizers): +range on ranged attacks, a minimum range, no melee.
  // Checked beside each range gate below, once the weapon itself is known to be legal.
  const stance = (intent.type === "request_attack" && !intent.tech) || intent.type === "request_barrage" ? attackStance({ state, targetId: intent.attacker, overlay }) : undefined;
  const stanceRefusal = (): string | undefined => {
    if (stance === undefined || (intent.type !== "request_attack" && intent.type !== "request_barrage")) return undefined;
    const verdict = checkAttackStance({ state, attacker: intent.attacker, weapon: intent.weapon, profile: intent.profile, target: intent.target, stance });
    return verdict.ok ? undefined : verdict.error;
  };
  const rangeBonus = stance !== undefined && stance.rangeBonus > 0 ? { rangeBonus: stance.rangeBonus } : {};

  // ── Reaction-attack gates: Overwatch, or a shot a rule grants ──
  // A granted shot (Covering Fire, Impending Threat, Moving Target: `reactionShotGrant`,
  // `economy/reactionShot.ts`) is not Overwatch: it reaches as far as the weapon's Range (not its
  // Threat), and Ordnance's "cannot be used for OVERWATCH" does not bar it. Ordnance's other two clauses (Core p.105): "only be fired before
  // the user moves or takes any other actions on their turn" does not reach an off-turn reaction,
  // and "can't be used against targets in engagement with the user's mech" applies to any shot, so
  // the engagement check runs.
  if (intent.type === "request_attack" && intent.reaction) {
    const grant = reactionShotGrant(state, intent);
    const verb = grant?.verb ?? "overwatch";
    if (intent.target.kind !== "unit") {
      return rejected(state, "overwatch: a reaction must target a unit (not a bare cell)", "illegal");
    }
    const reactionStatus = checkReactionStatusLegality(state, intent.attacker);
    if (!reactionStatus.ok) return rejected(state, `${verb}: ${reactionStatus.error}`, "illegal");
    if (state.activation.heldActions[intent.attacker] !== undefined) {
      return rejected(state, `${verb}: ${intent.attacker} is holding a prepared action — no reactions until it triggers/lapses`, "illegal");
    }
    const reactionTarget = state.combatants[intent.target.id];
    if (reactionTarget && hasStatus(reactionTarget.statuses, DISENGAGED_STATUS)) {
      return rejected(state, `${verb}: ${reactionTarget.name} is Disengaged this turn — its movement doesn't provoke reactions`, "illegal");
    }
    if (reactionTarget && feintedBy(reactionTarget, intent.attacker)) {
      return rejected(state, `${verb}: ${reactionTarget.name} is feinting ${state.combatants[intent.attacker]?.name ?? intent.attacker} — its movement doesn't provoke their reactions`, "illegal");
    }
    const overwatchWeapon = checkAttackWeapon({ state, attacker: intent.attacker, weapon: intent.weapon });
    if (!overwatchWeapon.ok) return rejected(state, overwatchWeapon.error, "illegal");
    // A single weapon attack, granted or not, cannot fire a Superheavy weapon.
    const overwatchSuperheavy = checkSuperheavySkirmish({ state, attacker: intent.attacker, weapon: intent.weapon, profile: intent.profile, verb: "overwatch" });
    if (!overwatchSuperheavy.ok) return rejected(state, overwatchSuperheavy.error, "illegal");
    const reactionStance = stanceRefusal();
    if (reactionStance !== undefined) return rejected(state, `${verb}: ${reactionStance}`, "illegal");
    if (grant !== undefined) {
      const reach = checkAttackRange({ state, attacker: intent.attacker, target: intent.target.id, weapon: intent.weapon, profile: intent.profile, ...rangeBonus });
      if (!reach.ok) return rejected(state, `${verb}: ${reach.error}`, "range");
      const engaged = checkOrdnanceEngagement({ state, attacker: intent.attacker, target: intent.target.id, weapon: intent.weapon, profile: intent.profile });
      if (!engaged.ok) return rejected(state, `${verb}: ${engaged.error}`, "illegal");
    } else {
      const reach = checkOverwatchRange({ state, attacker: intent.attacker, target: intent.target.id, weapon: intent.weapon, profile: intent.profile });
      if (!reach.ok) return rejected(state, `${reach.error}${notCoveringFireNote(state, intent)}`, "range");
    }
    // Reaction LoS wall — RAW (Core p.64): the reaction attack is an ordinary attack requiring
    // "within [Range/Threat] and line of sight." Reaction targets are always units, so the unit
    // predicate applies as-is; Seeking/Arcing exemptions are checked inside it.
    const overwatchLos = checkAttackLoS({ state, attacker: intent.attacker, target: intent.target.id, weapon: intent.weapon, profile: intent.profile });
    if (!overwatchLos.ok) return rejected(state, overwatchLos.error, "range");
    const overwatchMode = checkWeaponMode({ state, attacker: intent.attacker, intent });
    if (!overwatchMode.ok) return rejected(state, overwatchMode.error, "illegal");
    const overwatchProfile = checkProfileByState({ state, attacker: intent.attacker, weapon: intent.weapon, profile: intent.profile, overlay });
    if (!overwatchProfile.ok) return rejected(state, overwatchProfile.error, "illegal");
    const overwatchCharge = checkWeaponRecharge({ state, attacker: intent.attacker, weapon: intent.weapon, profile: intent.profile });
    if (!overwatchCharge.ok) return rejected(state, overwatchCharge.error, "illegal");
    const overwatchLoaded = checkLoaded({ state, attacker: intent.attacker, weapon: intent.weapon, profile: intent.profile });
    if (!overwatchLoaded.ok) return rejected(state, overwatchLoaded.error, "illegal");
    const overwatchLimited = checkLimited({ state, attacker: intent.attacker, weapon: intent.weapon, profile: intent.profile });
    if (!overwatchLimited.ok) return rejected(state, overwatchLimited.error, "illegal");
  }

  // ── Standard (non-reaction) attack gates: range, Hidden, tech-immunity, self-target, ──
  // ── willing-ally, Ram size ──
  if (intent.type === "request_attack" && !intent.reaction) {
    if (!intent.tech) {
      const weaponVerdict = checkAttackWeapon({ state, attacker: intent.attacker, weapon: intent.weapon });
      if (!weaponVerdict.ok) return rejected(state, weaponVerdict.error, "illegal");
      // Tech-attack legality wall, SYMMETRIC direction. `checkAttackWeapon` above only
      // rejects an unknown weapon when the attacker HAS weapons (`weapons.length > 0`), so a
      // weaponless mech naming a tech-attack carrier id (e.g. `act_invade`) WITHOUT `tech: true`
      // would otherwise fall straight through and resolve as an ordinary attack against Evasion —
      // skipping Sensors range (`checkTechRange`), tech LoS (`checkTechLoS`), and
      // Biological/Shut-Down tech-immunity wholesale. Reject it here, symmetric with the
      // tech-direction wall above.
      if (intent.weapon !== undefined && legalTechAttackWeaponIds(state, intent.attacker).has(intent.weapon)) {
        return rejected(state, `attack: "${intent.weapon}" is a tech-attack id — fire it with tech: true`, "illegal");
      }
      // A Superheavy fires only in a Barrage. Only a Skirmish opener is gated: a lock continuation
      // and a reserved follow-up derive no action id (`actionIdForIntent`).
      if (actionId === "skirmish") {
        const superheavy = checkSuperheavySkirmish({ state, attacker: intent.attacker, weapon: intent.weapon, profile: intent.profile, verb: "skirmish" });
        if (!superheavy.ok) return rejected(state, superheavy.error, "illegal");
      }
    }
    // (TECH direction handled by the unconditional wall above, hoisted ahead of the
    // reaction/non-reaction split — see that block's comment for why.)
    let verdict;
    if (intent.tech) {
      verdict = checkTechRange({ state, attacker: intent.attacker, target: intent.target });
    } else if (intent.target.kind === "unit") {
      verdict = checkAttackRange({ state, attacker: intent.attacker, target: intent.target.id, weapon: intent.weapon, profile: intent.profile, ...rangeBonus });
    } else {
      verdict = checkAttackCellRange({ state, attacker: intent.attacker, cell: intent.target.cell as Cell, weapon: intent.weapon, profile: intent.profile, ...rangeBonus });
    }
    if (!verdict.ok) return rejected(state, verdict.error, "range");
    const standardStance = stanceRefusal();
    if (standardStance !== undefined) return rejected(state, standardStance, "illegal");
    if (intent.tech && intent.target.kind === "unit") {
      const losVerdict = checkTechLoS({ state, actor: intent.attacker, target: intent.target.id, verb: "invade" });
      if (!losVerdict.ok) return rejected(state, losVerdict.error, "range");
    }
    // Weapon (ranged + melee) attack-legality LoS wall — RAW gates both on "within Range/Threat
    // and line of sight" (Core p.64).
    if (!intent.tech && intent.target.kind === "unit") {
      const weaponLosVerdict = checkAttackLoS({ state, attacker: intent.attacker, target: intent.target.id, weapon: intent.weapon, profile: intent.profile });
      if (!weaponLosVerdict.ok) return rejected(state, weaponLosVerdict.error, "range");
    }
    // A multi-target weapon's extra targets (`alsoTargets`, the Sharanga Missiles): each is gated
    // like the first, and only a weapon whose entry carries `attacksTargets` may name them.
    if (intent.alsoTargets !== undefined && intent.alsoTargets.length > 0) {
      const also = checkAlsoTargets({ state, intent, overlay, rangeBonus });
      if (also !== undefined) return rejected(state, also.error, also.category);
    }
    // The Ram Cannon's ranged-or-melee pick (`WeaponProfile.eitherMode`): melee reaches only Threat,
    // and the weapon can't be used both ways in one turn.
    if (!intent.tech) {
      const modeVerdict = checkWeaponMode({ state, attacker: intent.attacker, intent });
      if (!modeVerdict.ok) return rejected(state, modeVerdict.error, "illegal");
    }
    // Blast/area-origin LoS wall — RAW (Core p.64): an area attack's template is "drawn from a
    // point within Range and line of
    // sight." Gates the ORIGIN cell before the template fans out, mirroring the unit LoS check
    // just above; Seeking/Arcing exemptions are checked inside the predicate.
    if (!intent.tech && intent.target.kind !== "unit") {
      const cellLosVerdict = checkAttackCellLoS({ state, attacker: intent.attacker, cell: intent.target.cell as Cell, weapon: intent.weapon, profile: intent.profile });
      if (!cellLosVerdict.ok) return rejected(state, cellLosVerdict.error, "range");
    }
    if (!intent.tech) {
      const profileVerdict = checkProfileByState({ state, attacker: intent.attacker, weapon: intent.weapon, profile: intent.profile, overlay });
      if (!profileVerdict.ok) return rejected(state, profileVerdict.error, "illegal");
      const chargeVerdict = checkWeaponRecharge({ state, attacker: intent.attacker, weapon: intent.weapon, profile: intent.profile });
      if (!chargeVerdict.ok) return rejected(state, chargeVerdict.error, "illegal");
      const loadedVerdict = checkLoaded({ state, attacker: intent.attacker, weapon: intent.weapon, profile: intent.profile });
      if (!loadedVerdict.ok) return rejected(state, loadedVerdict.error, "illegal");
      const limitedVerdict = checkLimited({ state, attacker: intent.attacker, weapon: intent.weapon, profile: intent.profile });
      if (!limitedVerdict.ok) return rejected(state, limitedVerdict.error, "illegal");
      if (releaseCtx === undefined) {
        const ordnanceVerdict = checkOrdnance({ state, attacker: intent.attacker, weapon: intent.weapon, profile: intent.profile, midAttack });
        if (!ordnanceVerdict.ok) return rejected(state, ordnanceVerdict.error, "illegal");
        if (intent.target.kind === "unit") {
          const ordnanceEngagement = checkOrdnanceEngagement({ state, attacker: intent.attacker, target: intent.target.id, weapon: intent.weapon, profile: intent.profile });
          if (!ordnanceEngagement.ok) return rejected(state, ordnanceEngagement.error, "illegal");
        }
      }
    }
    if (intent.target.kind === "unit") {
      const tgt = state.combatants[intent.target.id];
      if (tgt && hasStatus(tgt.statuses, HIDDEN_STATUS)) {
        return rejected(state, `${tgt.name} is Hidden — can't be targeted directly (area attacks still reach it; the GM can override it)`, "illegal");
      }
      // Blanket-immunity targeting gate (Stasis Generator's "can't be targeted … by any other
      // character or effect"). A content-authorable facet ({kind:"all"}
      // immunity, on the Stasis marker), unlike Hidden's bespoke name-key above. Covers weapon +
      // tech attacks + Ram (all request_attack); area attacks still reach it (cell-target, not a
      // unit); the GM can override it.
      if (tgt && hasBlanketImmunity({ state, targetId: tgt.id, overlay })) {
        return rejected(state, `${tgt.name} can't be targeted — under a blanket immunity (Stasis); area attacks still reach it, the GM can override it`, "illegal");
      }
    }
    if (intent.tech && intent.target.kind === "unit") {
      const tgt = state.combatants[intent.target.id];
      if (tgt && immuneToTechAction({ state, targetId: tgt.id, actionId: intent.weapon ?? "act_invade", overlay })) {
        return rejected(state, `${tgt.name} is immune to tech actions/attacks (Shut Down/Biological) — this is a no-op (the GM can override it)`, "illegal");
      }
      const refusal = (intent.weapon ?? "act_invade") === "act_invade" ? invadeAnswerRefusal({ state, attackerId: intent.attacker, targetId: intent.target.id, choices: intent.choices, overlay }) : undefined;
      if (refusal !== undefined) return rejected(state, refusal, "illegal");
    }
    if (intent.tech && intent.target.kind === "unit" && intent.target.id === intent.attacker) {
      return rejected(state, `tech: ${intent.attacker} can't target itself (characters can't target themselves)`, "illegal");
    }
    if (intent.tech && intent.target.kind === "unit") {
      const tgt = state.combatants[intent.target.id];
      const atk = state.combatants[intent.attacker];
      if (tgt && atk && atk.base.allegiance !== undefined && tgt.base.allegiance !== undefined && sideOf(tgt) === sideOf(atk)) {
        return rejected(state, "tech attack on a same-side target: willing-ally invade not yet supported (every same-side tech attack is refused, not only Invade)", "illegal");
      }
    }
    if (intent.weapon === "act_ram" && intent.target.kind === "unit") {
      const sizeVerdict = checkRamSize({ state, attacker: intent.attacker, target: intent.target.id, ...(stance !== undefined ? { stance } : {}) });
      if (!sizeVerdict.ok) return rejected(state, sizeVerdict.error, "illegal");
    }
  }

  // ── Barrage per-shot gates ──
  if (intent.type === "request_barrage") {
    if (intent.target.kind === "unit") {
      const tgt = state.combatants[intent.target.id];
      // Blanket-immunity targeting gate — barrage is a separate intent shape from request_attack,
      // so its unit-target path needs the same can't-be-targeted gate.
      if (tgt && hasBlanketImmunity({ state, targetId: tgt.id, overlay })) {
        return rejected(state, `${tgt.name} can't be targeted — under a blanket immunity (Stasis); the GM can override it`, "illegal");
      }
    }
    const fired = lock?.weapons ?? [];
    // Mount commitment (`decisions/0049`, 2026-09-24 amendment): the opener names its mounts
    // (required whenever the attacker has a weapon; a weaponless Barrage's fallback strike has no
    // mount); a continuation that repeats them must repeat the lock's set exactly.
    if (!midAttack && intent.mounts === undefined && barrageLoadout(state, intent.attacker).weapons.length > 0) {
      return rejected(state, `barrage: the opener names its committed mounts (mounts: one or two mount keys, or a Superheavy's mount alone)`, "illegal");
    }
    if (midAttack && intent.mounts !== undefined) {
      const held = lock?.mounts ?? [];
      const same = intent.mounts.length === held.length && intent.mounts.every((m) => held.includes(m));
      if (!same) return rejected(state, `barrage: the committed mounts are ${held.join(", ") || "none"} — they can't change mid-Barrage`, "illegal");
    }
    const legality = midAttack
      ? checkBarrageContinuation({
          state,
          attackerId: intent.attacker,
          weapon: intent.weapon,
          fired,
          ...(lock?.mounts !== undefined ? { mounts: lock.mounts } : {}),
        })
      : checkBarrageOpener(state, intent.attacker, intent.weapon);
    if (!legality.ok) return rejected(state, legality.error, "illegal");
    if (!midAttack && intent.mounts !== undefined) {
      const commit = checkBarrageCommit({ state, attackerId: intent.attacker, weapon: intent.weapon, mounts: intent.mounts });
      if (!commit.ok) return rejected(state, commit.error, "illegal");
    }
    const rangeV =
      intent.target.kind === "unit"
        ? checkAttackRange({ state, attacker: intent.attacker, target: intent.target.id, weapon: intent.weapon, profile: intent.profile, ...rangeBonus })
        : checkAttackCellRange({ state, attacker: intent.attacker, cell: intent.target.cell as Cell, weapon: intent.weapon, profile: intent.profile, ...rangeBonus });
    if (!rangeV.ok) return rejected(state, rangeV.error, "range");
    const barrageStance = stanceRefusal();
    if (barrageStance !== undefined) return rejected(state, barrageStance, "illegal");
    // Barrage LoS wall — barrage shots never route through the non-reaction `request_attack`
    // block's LoS gate above (a separate `request_barrage` intent shape), so unit AND cell targets
    // would otherwise be ungated; RAW (Core p.64) requires "Range/Threat and line of sight" the
    // same as any other weapon attack.
    const losV =
      intent.target.kind === "unit"
        ? checkAttackLoS({ state, attacker: intent.attacker, target: intent.target.id, weapon: intent.weapon, profile: intent.profile })
        : checkAttackCellLoS({ state, attacker: intent.attacker, cell: intent.target.cell as Cell, weapon: intent.weapon, profile: intent.profile });
    if (!losV.ok) return rejected(state, losV.error, "range");
    // A multi-target weapon's extra targets ride the Barrage shot too, gated like a Skirmish's.
    if (intent.alsoTargets !== undefined && intent.alsoTargets.length > 0) {
      const shot: RequestAttack = { type: "request_attack", attacker: intent.attacker, target: intent.target, weapon: intent.weapon, accuracy: intent.accuracy, alsoTargets: intent.alsoTargets, ...(intent.profile !== undefined ? { profile: intent.profile } : {}) };
      const also = checkAlsoTargets({ state, intent: shot, overlay, rangeBonus });
      if (also !== undefined) return rejected(state, also.error, also.category);
    }
    const barrageProfile = checkProfileByState({ state, attacker: intent.attacker, weapon: intent.weapon, profile: intent.profile, overlay });
    if (!barrageProfile.ok) return rejected(state, barrageProfile.error, "illegal");
    const barrageCharge = checkWeaponRecharge({ state, attacker: intent.attacker, weapon: intent.weapon, profile: intent.profile });
    if (!barrageCharge.ok) return rejected(state, barrageCharge.error, "illegal");
    const barrageLimited = checkLimited({ state, attacker: intent.attacker, weapon: intent.weapon, profile: intent.profile });
    if (!barrageLimited.ok) return rejected(state, barrageLimited.error, "illegal");
    const barrageOrdnance = checkOrdnance({ state, attacker: intent.attacker, weapon: intent.weapon, profile: intent.profile, midAttack });
    if (!barrageOrdnance.ok) return rejected(state, barrageOrdnance.error, "illegal");
    if (intent.target.kind === "unit") {
      const barrageOrdnanceEngagement = checkOrdnanceEngagement({ state, attacker: intent.attacker, target: intent.target.id, weapon: intent.weapon, profile: intent.profile });
      if (!barrageOrdnanceEngagement.ok) return rejected(state, barrageOrdnanceEngagement.error, "illegal");
    }
  }

  // ── Stabilize / Overcharge legality ──
  if (intent.type === "request_stabilize") {
    const legality = checkStabilizeLegality(state, intent);
    if (!legality.ok) return rejected(state, legality.error, "illegal");
  }
  if (intent.type === "request_overcharge") {
    const legality = checkOverchargeLegality(state, intent);
    if (!legality.ok) return rejected(state, legality.error, "illegal");
  }

  // ── Full Tech opener/continuation gates ──
  // A Full Tech pick is EXACTLY the same single-sub shape a standalone Quick Tech resolves — there
  // is no bundle/loop left to gate, just one routed intent. The walls
  // below are the SAME self-target/tech-immunity/willing-ally/Sensors-range/LoS predicates the
  // standalone `request_attack`(tech)/`request_activate` blocks call a few lines below/above —
  // reused (same predicate functions), run once (no `subActions` loop left to run them twice).
  if (intent.type === "request_full_tech") {
    const carrier = { state, actor: intent.actor, overlay };
    const legality = midAttack ? checkFullTechContinuation(intent.option, lock?.weapons ?? [], carrier) : checkFullTechOpener(intent.option, carrier);
    if (!legality.ok) return rejected(state, legality.error, "illegal");
    const subIntent = fullTechSubIntent(intent);
    // A carried Quick Tech action (Crush Targeting) gets the walls a standalone activation of it
    // gets at the room's gate ladder: its Recharge and frequency, and its frozen reach with line
    // of sight.
    if (subIntent.type === "request_activate" && quickTechOption(intent.option) === undefined) {
      const granted = checkGrantedActionLegality({ state, actor: intent.actor, action: intent.option, overlay });
      if (!granted.ok) return rejected(state, `full tech: ${granted.error}`, "illegal");
      const reach = checkActivateUnitReach({ state, actor: intent.actor, action: intent.option, target: intent.target });
      if (!reach.ok) return rejected(state, `full tech: ${reach.error}`, "illegal");
    }
    const unarmedBy = subIntent.type === "request_attack" ? cannotMakeAttacks({ state, actorId: intent.actor, overlay }) : undefined;
    if (unarmedBy !== undefined) return rejected(state, cannotAttackRefusal(state, { actorId: intent.actor, source: unarmedBy }), "illegal");
    const subActionId = subIntent.type === "request_attack" ? subIntent.weapon ?? "act_invade" : subIntent.action;
    const subTargetId = subIntent.type === "request_attack" ? (subIntent.target.kind === "unit" ? subIntent.target.id : undefined) : subIntent.target;
    if (subTargetId !== undefined && immuneToTechAction({ state, targetId: subTargetId, actionId: subActionId, overlay })) {
      const subTgt = state.combatants[subTargetId];
      return rejected(state, `full tech: ${subTgt?.name ?? subTargetId} is immune to tech actions/attacks (Shut Down/Biological)`, "illegal");
    }
    if (subIntent.type === "request_attack" && subActionId === "act_invade" && subTargetId !== undefined) {
      const refusal = invadeAnswerRefusal({ state, attackerId: intent.actor, targetId: subTargetId, choices: intent.choices, overlay });
      if (refusal !== undefined) return rejected(state, `full tech: ${refusal}`, "illegal");
    }
    if (subTargetId !== undefined && subTargetId === intent.actor) {
      return rejected(state, `full tech: ${intent.actor} can't target itself (characters can't target themselves)`, "illegal");
    }
    if (subIntent.type === "request_attack" && subIntent.tech && subTargetId !== undefined) {
      const subTgt = state.combatants[subTargetId];
      const subAtk = state.combatants[subIntent.attacker];
      if (subTgt && subAtk && subAtk.base.allegiance !== undefined && subTgt.base.allegiance !== undefined && sideOf(subTgt) === sideOf(subAtk)) {
        return rejected(state, "full tech: willing-ally invade not yet supported", "illegal");
      }
    }
    let verdict: ReturnType<typeof checkTechRange> | undefined;
    if (subIntent.type === "request_attack" && subIntent.tech) {
      verdict = checkTechRange({ state, attacker: subIntent.attacker, target: subIntent.target });
      if (verdict.ok && subIntent.target.kind === "unit") {
        const losVerdict = checkTechLoS({ state, actor: subIntent.attacker, target: subIntent.target.id, verb: "invade" });
        if (!losVerdict.ok) return rejected(state, `full tech: ${losVerdict.error}`, "range");
      }
    } else if (subIntent.type === "request_activate" && subIntent.action === "act_bolster") {
      if (subIntent.target === undefined) {
        return rejected(state, "bolster: needs a target character (within Sensors) to enhance", "illegal");
      }
      const benefitVerdict = checkJammedBenefitLegality(state, subIntent.target);
      if (!benefitVerdict.ok) return rejected(state, `full tech: ${benefitVerdict.error}`, "illegal");
      verdict = checkTechRange({ state, attacker: subIntent.actor, target: { kind: "unit", id: subIntent.target } });
    } else if (subIntent.type === "request_activate" && subIntent.action === "act_scan") {
      if (subIntent.target === undefined) {
        return rejected(state, "scan: needs a target character (within Sensors and line of sight)", "illegal");
      }
      verdict = checkTechRange({ state, attacker: subIntent.actor, target: { kind: "unit", id: subIntent.target } });
      if (verdict.ok) {
        const losVerdict = checkTechLoS({ state, actor: subIntent.actor, target: subIntent.target, verb: "scan" });
        if (!losVerdict.ok) return rejected(state, `full tech: ${losVerdict.error}`, "range");
      }
    } else if (subIntent.type === "request_activate" && subIntent.action === "act_lock_on") {
      if (subIntent.target === undefined) {
        return rejected(state, "lock on: needs a target character (within Sensors and line of sight)", "illegal");
      }
      verdict = checkTechRange({ state, attacker: subIntent.actor, target: { kind: "unit", id: subIntent.target } });
      if (verdict.ok) {
        const losVerdict = checkTechLoS({ state, actor: subIntent.actor, target: subIntent.target, verb: "lock on" });
        if (!losVerdict.ok) return rejected(state, `full tech: ${losVerdict.error}`, "range");
      }
    }
    if (verdict && !verdict.ok) return rejected(state, `full tech: ${verdict.error}`, "range");
  }

  // ── Tech-immunity for targeted tech activates (Scan/Lock On/Bolster) ──
  if (intent.type === "request_activate" && (intent.action === "act_scan" || intent.action === "act_lock_on" || intent.action === "act_bolster") && intent.target !== undefined) {
    if (intent.target === intent.actor) {
      return rejected(state, `${intent.action}: ${intent.actor} can't target itself (characters can't target themselves)`, "illegal");
    }
    const tgt = state.combatants[intent.target];
    if (tgt && hasBlanketImmunity({ state, targetId: tgt.id, overlay })) {
      return rejected(state, `${tgt.name} can't be targeted — under a blanket immunity (Stasis); the GM can override with a manual command`, "illegal");
    }
    if (tgt && immuneToTechAction({ state, targetId: tgt.id, actionId: intent.action, overlay })) {
      return rejected(state, `${tgt.name} is immune to tech actions/attacks (Shut Down/Biological) — this is a no-op (the GM can override with a manual command)`, "illegal");
    }
  }

  // ── Bolster / Scan / Lock On standalone gates ──
  if (intent.type === "request_activate" && intent.action === "act_bolster") {
    if (intent.target === undefined) {
      return rejected(state, "bolster: needs a target character (within Sensors) to enhance", "illegal");
    }
    const benefitVerdict = checkJammedBenefitLegality(state, intent.target);
    if (!benefitVerdict.ok) return rejected(state, benefitVerdict.error, "illegal");
    const verdict = checkTechRange({ state, attacker: intent.actor, target: { kind: "unit", id: intent.target } });
    if (!verdict.ok) return rejected(state, verdict.error, "range");
  }
  if (intent.type === "request_activate" && intent.action === "act_scan") {
    if (intent.target === undefined) {
      return rejected(state, "scan: needs a target character (within Sensors and line of sight)", "illegal");
    }
    const rangeVerdict = checkTechRange({ state, attacker: intent.actor, target: { kind: "unit", id: intent.target } });
    if (!rangeVerdict.ok) return rejected(state, rangeVerdict.error, "range");
    const losVerdict = checkTechLoS({ state, actor: intent.actor, target: intent.target, verb: "scan" });
    if (!losVerdict.ok) return rejected(state, losVerdict.error, "range");
  }
  if (intent.type === "request_activate" && intent.action === "act_lock_on") {
    if (intent.target === undefined) {
      return rejected(state, "lock on: needs a target character (within Sensors and line of sight)", "illegal");
    }
    const rangeVerdict = checkTechRange({ state, attacker: intent.actor, target: { kind: "unit", id: intent.target } });
    if (!rangeVerdict.ok) return rejected(state, rangeVerdict.error, "range");
    const losVerdict = checkTechLoS({ state, actor: intent.actor, target: intent.target, verb: "lock on" });
    if (!losVerdict.ok) return rejected(state, losVerdict.error, "range");
  }

  // ── Carrier-ownership wall — the `request_activate` twin of the tech-attack wall above.
  // Referee-side, role-blind, NO GM bypass: an NPC's own carried actions are carried by
  // definition, so a GM-attributed activate of its own NPC's action still passes; a non-carried
  // activate is rejected identically for a player AND a GM. GM fiat for a non-carried action lives
  // in the existing force tier (`edit_combatant_base`/`place_token`), not a bypass here, matching
  // `legalTechAttackWeaponIds`'s own placement/bypass shape (no role check, its GM valve is the
  // separate `gm_force` wrapper). Ringing HERE rather than room-side matters:
  // `packages/ai`'s `DirectRefereeHandle` dispatches `request_activate` straight to this SAME
  // function, bypassing the room's shared gate ladder entirely.
  //
  // **Skipped deliberately on the release path** (`releaseCtx !== undefined`). The room's release
  // ladder skips every OTHER paid-at-prepare gate too. A held granted action's carrier ownership
  // is checked once, at `prepare` time, by `refereeCommand.ts`'s `preparePreApplyGate`, and
  // `resolveRelease` re-verifies only that the release names the SAME id that was readied. Since
  // `held.readiedActionId` can't change between prepare and release, re-checking here would be
  // redundant, not safer — and would wrongly re-derive the query against a possibly-stale
  // `overlay` for a hold armed earlier in the encounter.
  if (releaseCtx === undefined && intent.type === "request_activate") {
    if (!isActivateActionLegal({ state, actorId: intent.actor, queriedId: intent.action, overlay })) {
      return rejected(state, `activate: ${intent.actor} does not carry "${intent.action}"`, "illegal");
    }
    // An action a deployed drone carries for its allies (Restock) names that drone as its target.
    const deployedRefusal = deployedActionRefusal({ state, actorId: intent.actor, action: intent.action, target: intent.target, overlay });
    if (deployedRefusal !== undefined) return rejected(state, deployedRefusal, "illegal");
  }
  return undefined;
}

/**
 * **The resolution half**: the `resolve()`-family pass, the economy sibling fold under `policy`,
 * and the followup chain. It re-checks no legality. Two callers: the gameplay door
 * ({@link adjudicateIntent}, after {@link checkIntentLegality}) and the GM override
 * (`./refereeForce.ts`, after its structural check).
 */
export function resolveIntentPlan(args: { state: EncounterState; intent: GenericResolutionIntent; ctx: RefereeIntentCtx; facts: IntentFacts; policy: SpendPolicy }): RefereePlan {
  const { state, intent, ctx, facts, policy } = args;
  const { actorId, releaseCtx, dice, overlay } = ctx;
  const { turn, lock, midAttack, cost, actionId } = facts;

  // ── The resolve()-family pass itself (the recorded-dice replay) ──
  // The intent's one event loop, over its one recording source (`decisions/0212` item 6): every
  // resolution, nested forced attack and followup below draws from `loop.dice`, so a halt anywhere
  // echoes every die drawn before it.
  const { loop, recordingDice } = ctx.intentLoop ?? createIntentLoop(intent, dice);
  // One loop per intent: a second declaration on a used loop would nest under the first.
  if (loop.log.length > 0) throw new Error("resolveIntentPlan: this IntentLoop already ran an intent; make one per intent");
  // The declaration roots the intent's cause tree (`decisions/0212` item 8). It stays open for the
  // whole intent, so the resolution, its forced attacks, its followups and the track observer after
  // it all descend from it.
  loop.open(declarationOf(intent, facts));
  // Drone fields that act before an attack (Sentinel) fire first, against the attacker, and the
  // attack resolves against what they leave (`auraBeforeAttackPrePass`).
  const prePass = auraBeforeAttackPrePass(state, { attackerId: attackerOfAttack(intent), actorId, dice: loop.dice, overlay, loop });
  if (prePass?.attackPrevented === true) return prePass.plan;
  const resolveState = prePass?.plan.finalState ?? state;
  let result: ResolveResult;
  const cfg = { interactive: true, directionAnswerable: intent.type === "request_activate" ? true : intentCarriesReactionToward(intent), overlay, loop };
  try {
    // Interactive for `choose` prompts always — but a SPATIAL halt is only raisable when this
    // intent's own shape can carry the answer back
    // (see `intentCarriesReactionToward`). A Barrage shot can't, so its declared-phase
    // `move_self_toward_target` degrades to the non-interactive default with a warning instead
    // of hanging the encounter on a prompt with nowhere to answer into.
    //
    // `request_activate` is UNCONDITIONALLY direction-answerable, never routed through
    // `intentCarriesReactionToward` — REAL BUG FOUND (`npc-features-fielded-roster` Phase 6.1):
    // `intentCarriesReactionToward`'s table answers "does this intent carry a `reactionToward`
    // field" (the attack-spine's reaction-answer slot), and `RequestActivate` never had one, so it
    // read `false` — but `RequestActivate` carries its OWN, DIFFERENT `toward` field (already used
    // for a line/cone area template's aim), which `resolveActivate` (`activate.ts`) reads into
    // `ctx.toward` UNCONDITIONALLY, independent of this flag. So Charge's own
    // `move_self_toward_target{toward:"chosen"}` — a Quick ACTION, not an attack/barrage — silently
    // took the non-interactive "could not place" no-op through this exact path instead of ever
    // raising its direction prompt, invisible until Charge needed a genuine interactive round trip
    // through `request_activate` for the first time. `request_stabilize`/`request_overcharge`/
    // `request_full_tech` correctly stay routed through the generic table (none of them carries a
    // spatial-answer field at all).
    if (intent.type === "request_activate") {
      result = resolveActivate({ state: resolveState, intent, input: diceInputSource(loop.dice), actorId, effects: cfg });
    } else if (intent.type === "request_stabilize") {
      result = resolveStabilize({ state: resolveState, intent, actorId, loop });
    } else if (intent.type === "request_overcharge") {
      result = resolveOvercharge({ state: resolveState, intent, dice: loop.dice, actorId, overlay, interactive: cfg.interactive === true, loop });
    } else if (intent.type === "request_barrage") {
      const shot: RequestAttack = {
        type: "request_attack",
        attacker: intent.attacker,
        weapon: intent.weapon,
        target: intent.target,
        accuracy: intent.accuracy,
        ...(intent.alsoTargets !== undefined && intent.alsoTargets.length > 0 ? { alsoTargets: intent.alsoTargets } : {}),
        ...(intent.profile !== undefined ? { profile: intent.profile } : {}),
        ...(intent.choices ? { choices: intent.choices } : {}),
        ...(intent.toward !== undefined ? { toward: intent.toward } : {}),
        ...(intent.sites !== undefined ? { sites: intent.sites } : {}),
        // Forward the recorded spatial answer onto the synthesized shot — this is what makes a
        // Barrage's declared-phase direction/magnitude halt answerable at all. Read through
        // `recordedReactionToward` (not `intent.reactionToward`) so the threading and
        // `REACTION_TOWARD_CARRIERS` can only ever move together.
        ...(recordedReactionToward(intent) !== undefined ? { reactionToward: recordedReactionToward(intent) } : {}),
      };
      const shotResult = resolve({ state: resolveState, intent: shot, input: diceInputSource(loop.dice), actorId, effects: cfg });
      result = shotResult.kind === "resolved" ? { ...shotResult, command: { ...shotResult.command, kind: "barrage" } } : shotResult;
    } else if (intent.type === "request_full_tech") {
      // At one-sub-per-intent, Full Tech is payload-identical to a standalone Quick Tech —
      // synthesize the routed intent via the SAME `quickTechIntent` router a single Quick Tech
      // uses and call `resolve`/`resolveActivate` directly, relabeling the command's retained-log
      // kind as "full_tech" for CLI/audit purposes — the exact pattern the `request_barrage`
      // branch above uses for its synthesized shot.
      const subIntent = fullTechSubIntent(intent);
      // Same co-movement wiring as the Barrage branch above — inert while `RequestFullTech`
      // carries no `reactionToward` slot (so `cfg.directionAnswerable` is false for it too).
      const answer = recordedReactionToward(intent);
      if (answer !== undefined && subIntent.type === "request_attack") subIntent.reactionToward = answer;
      const subResult =
        subIntent.type === "request_attack"
          ? resolve({ state: resolveState, intent: subIntent, input: diceInputSource(loop.dice), actorId, effects: cfg })
          : resolveActivate({ state: resolveState, intent: subIntent, input: diceInputSource(loop.dice), actorId, effects: cfg });
      result = subResult.kind === "resolved" ? { ...subResult, command: { ...subResult.command, kind: "full_tech" } } : subResult;
    } else {
      result = resolve({ state: resolveState, intent, input: diceInputSource(loop.dice), actorId, effects: cfg });
    }
  } catch (err) {
    if (err instanceof ResolveError) return rejected(state, err.message, "unresolvable");
    throw err;
  }

  // Aux rider marker (Core p.70/p.71) — shared between a Barrage rider and a Skirmish's Aux rider.
  // RAW's rider is always the Auxiliary (`decisions/0049`, 2026-09-25 amendment): an Aux fired
  // after another weapon on its mount carries `noBonus`; an Aux fired FIRST carries nothing yet,
  // and if its Main follows, the Main's resolution names it as the rider (`about.auxRider`).
  let pairedAuxRider: string | undefined;
  if (result.kind === "resolved" && (intent.type === "request_barrage" || (intent.type === "request_attack" && !intent.reaction)) && intent.weapon !== undefined) {
    const riderArgs = { state, attackerId: intent.attacker, fired: midAttack ? lock?.weapons ?? [] : [], weaponKey: intent.weapon };
    if (isRiderShot(riderArgs)) result = { ...result, command: { ...result.command, noBonus: true } };
    pairedAuxRider = pairedAuxRiderOf(riderArgs);
  }

  // Forced attacks (`forced_attack` — Aggression, Avalanche Charge): resolved against the
  // post-resolution state and folded into THIS resolution, before the economy fold — which keys on
  // the original intent, so they spend nothing. A nested halt is the same whole-intent re-run.
  if ("forcedAttacks" in result && result.forcedAttacks !== undefined && result.forcedAttacks.length > 0) {
    result = resolveForcedAttacks({
      state,
      result,
      input: diceInputSource(loop.dice, loop),
      actorId,
      effects: cfg,
      loop,
      choices: "choices" in intent ? intent.choices : undefined,
      // An Overwatch shot spends its reactor's reaction; the swing back must not offer it Brace.
      ...(cost.reaction ? { preSpent: [{ combatantId: cost.actor, name: cost.reactionId ?? "reaction" }] } : {}),
    });
  }

  if (result.kind === "needs_decision") {
    // The "whole-resolution re-run" halt mode — no entries; the room echoes the intent (dice
    // drawn so far ridden on `recordedDice`) and re-submits on answer.
    const echoedIntent = { ...intent, recordedDice: recordingDice.log() };
    return needsDecisionPlan(state, result, echoedIntent);
  }

  // An activation's own walk that provokes reactions (Impale) halts for them before anything commits.
  const provoked =
    result.movePath?.provokesReactions === true
      ? provokedReactions(state, { movePath: result.movePath, choices: "choices" in intent ? intent.choices : undefined, loop, ctx })
      : { declines: [] };
  if ("halt" in provoked) {
    const echoedIntent = { ...intent, recordedDice: recordingDice.log() };
    return needsDecisionPlan(state, provoked.halt, echoedIntent);
  }

  // **The free-no-op fence** (`decisions/0162`) — narrowed, never deleted. An activation that
  // gated out (every clause's condition unmet) is rejected, free; one that declined (took an
  // authored empty `choose` option) is a legitimate no-op plan: nothing to apply, broadcast or
  // charge. An activation that
  // RAN and emitted nothing (a grenade at empty ground, a roll that missed) falls through to the
  // economy fold and broadcasts its zero-step resolution, so the player sees what their action
  // bought. Only `resolveActivate` sets `disposition`; every other resolver keeps the step-count
  // test (none of them can reach zero steps — see the tracker). `pre-activation-affordances` D3
  // builds on this fence standing ahead of the fold: a no-op never commits the activation.
  const disposition = "disposition" in result ? result.disposition : undefined;
  if (disposition === "gated" && intent.type === "request_activate") {
    // Free, but never silent (`decisions/0162`): every clause's condition was false, so say which
    // requirement failed. The bar greys only self-decidable gates (`conditionsAllUnmet` degrades
    // open without a bound target), so a target-relational one (Patch, Corrective) lands here.
    const name = state.combatants[intent.actor]?.name ?? intent.actor;
    const why = result.unmetCondition !== undefined ? ` (requires: ${result.unmetCondition})` : "";
    return rejected(state, `${intent.action}: ${name} can't use this now${why}`, "illegal");
  }
  if (disposition === "gated" || disposition === "declined") return { entries: [], finalState: state };
  if (disposition === undefined && result.command.steps.length === 0) return prePass?.plan ?? { entries: [], finalState: state };

  // ── The economy sibling fold. **Batch order is load-bearing**: the batch's `commands` array ──
  // ── order is part of the retained log, so byte-identity depends on it. ──
  // Whether the turn budget pays (`spendsThisTurn`). A forced action raises its costs on or off its
  // turn, and settlement floors them (D5, `decisions/0209` §3): off its turn it spends no action.
  const acting = spendsThisTurn(state, policy);
  const raises = acting || policy.kind === "forced";
  const spend = releaseCtx === undefined && raises && (cost.points > 0 || cost.reaction);
  // A forced shot opens no attack lock: a lock would wall the GM's next forced action on the unit.
  const locks = acting && policy.kind === "rules";
  const siblings: EncounterCommand[] = [];
  // The action's economy costs (`event-engine-costs` D1, 5.1), raised in today's batch order while
  // the declaration is open, and settled once below into their sibling commands (D4).
  const costs: EconomyCostEvent[] = [];
  if (intent.type === "request_overcharge" && acting) {
    siblings.push({ type: "grant_overcharge", actorId, combatantId: intent.actor });
  } else if (intent.type === "request_activate" && acting && grantsFreeQuickAction(result.command.steps, intent.actor)) {
    siblings.push({ type: "grant_action_allowance", actorId, combatantId: intent.actor });
  } else if (spend) {
    costs.push({
      kind: "action_points",
      carrier: cost.actor,
      points: cost.points,
      ...(cost.reaction ? { reaction: cost.reactionId !== undefined && cost.reactionId !== "overwatch" ? { name: cost.reactionId } : {} } : {}),
      ...(actionId !== null ? { actionId } : {}),
    });
    // A Quick Tech use also stamps its per-option slot (a zero-point spend moves only the ledger),
    // so the repeat wall refuses the SAME option twice but not a different one (Core p.69).
    const optionKey = quickTechOptionLedgerId(intent, actionId);
    if (optionKey !== undefined) costs.push({ kind: "action_points", carrier: cost.actor, points: 0, actionId: optionKey });
  }
  // The cost-earmark consume — folded as a sibling of the accepted intent, exactly like the
  // `spend_action` it replaces: `intentCost` sets `freeUseVerb` only because it found and applied
  // a held earmark, so this pays for that discount by burning the earmark in the same batch.
  // **Same-batch matters** — the discount and the consume must land or undo together, or an undone
  // free Boost leaves the earmark spent.
  //
  // Branches on `cost.freeUseVerb`, **NEVER** on `cost.points === 0`: plenty of intents are free
  // for ordinary reasons (a protocol, a reaction, a Barrage continuation) and burning an earmark
  // for one of those would spend a resource nobody held.
  //
  // Boost's absence from this path is NOT a gap: it never reaches `intentCost` at all (it is a
  // domain command), so its earmark is consumed inside the `boost` reducer case with its own
  // compound inverse.
  if (releaseCtx === undefined && raises && cost.freeUseVerb !== undefined) {
    costs.push({ kind: "earmark", carrier: cost.actor, verb: cost.freeUseVerb });
  }
  // A forced activation (`decisions/0209`) draws on its pools on or off its turn, floored at the cap
  // ("pools floor at zero"); only on its own turn does it stamp the once-per-turn ledgers. Both are
  // settlement's call (D5).
  if (releaseCtx === undefined && raises && intent.type === "request_activate") {
    // **One parser at the door** (`decisions/0176` ruling 3, `projects/affordance-table.md` 1.4):
    // the cap and the ledger slot both come off the carrier's ONE frozen record, never
    // `intent.action` raw. `intent.action` is legitimately bare or composite and which is correct
    // is carrier-class-determined, so a raw read found no cap for a bare granted fire, folded no
    // `spend_action_use` at all, and left the frequency cap unenforced forever — while
    // `checkGrantedActionLegality`'s READ half was already class-resolved, so the gate was
    // checking a slot nothing wrote. Read and write name one slot only when BOTH go through this
    // door; that pairing is the whole of 1.4.
    const grantedAffordance = affordanceFor({ state, intent, overlay });
    const capKey = grantedAffordance.key;
    const protocol = isProtocolAction(intent.actor, intent.action, overlay);
    // Rate and pool decrements — independent substrates from the once-per-turn `actionUses`
    // ledger below, so this is NOT an `else` off it: a 0-cost scene/round-capped granted action
    // needs BOTH folded (charge count AND once-per-turn wall are separate RAW rules over the same
    // action).
    costs.push(...affordanceUses({ affordance: grantedAffordance, combatantId: intent.actor }));
    if (protocol) {
      // Every Protocol activation records on the protocol-safe `protocolUses` sibling, never
      // `actionUses` (RAW Core p.62: each Protocol once per turn, capped or not — no shipped
      // Protocol freezes a cap). `capKey`, not `intent.action`: `checkGrantedActionLegality`'s
      // Protocol wall reads `canUseProtocolAction(turn.budget, key, …)` on the class-resolved key,
      // so a bare and a composite fire share one slot.
      costs.push({ kind: "action_points", carrier: intent.actor, points: 0, protocolActionId: capKey });
    } else if (!spend) {
      // A 0-cost (free) granted activate still needs its once-per-turn `actionUses` entry folded
      // HERE — the `spend`-gated branch far above only fires when the activation costs an action
      // point (or a reaction), so a genuinely free granted action would otherwise never record a
      // use. `actionIdForIntent` returns the affordance key (`capKey`) for every non-Protocol
      // granted activate unconditionally, so the read-side wall (`checkActionUseLegality`) checks
      // this key for ALL of them, whichever id shape the caller named — stamping `intent.action`
      // raw here would give a bare and a composite fire two slots. **Narrowing this branch below
      // that read-side check silently makes every uncapped or scene/round-capped free granted
      // action infinitely repeatable in one turn** despite the wall "gating" it, because an always-empty ledger never rejects. Hence deliberately
      // unconditional on the record's rate or pool (or their absence), and independent of the
      // decrements above (all apply together when all are frozen).
      costs.push({ kind: "action_points", carrier: intent.actor, points: 0, actionId: capKey });
    }
  }
  if (releaseCtx !== undefined && intent.type === "request_activate") {
    // The release path's twin of the fold above — same door, same reason. A held granted action
    // was armed under `held.readiedActionId` and released naming that same id, so the shape it
    // carries is whatever the preparer emitted; resolving it here is what makes the released
    // fire burn the same slot the live fire would have.
    costs.push(...affordanceUses({ affordance: affordanceFor({ state, intent, overlay }), combatantId: intent.actor }));
  }
  siblings.push(...economyNow(actorId, costs, { state, policy, loop }));
  if ("reactionSpentBy" in result) siblings.push(...reactionSpendCommands(actorId, result.reactionSpentBy));
  if ("ordinalIncrement" in result && result.ordinalIncrement !== undefined) {
    result = { ...result, command: { ...result.command, steps: [...result.command.steps, result.ordinalIncrement] } };
  }
  if (result.kind === "resolved" && intent.type === "request_attack" && !intent.tech) {
    const latch = weaponModeLatchStep({ state, intent });
    if (latch !== undefined) result = { ...result, command: { ...result.command, steps: [...result.command.steps, latch] } };
  }
  if ("resourceCapConsumeSteps" in result && result.resourceCapConsumeSteps !== undefined && result.resourceCapConsumeSteps.length > 0) {
    result = { ...result, command: { ...result.command, steps: [...result.command.steps, ...result.resourceCapConsumeSteps] } };
  }
  if (releaseCtx !== undefined) {
    siblings.push({ type: "drop_prepare", actorId, combatantId: releaseCtx.preparer });
  }
  // Executioner's Backswing Cut consume: the follow-up spent the reservation, a `consume_status`
  // cost (D1) grounded as a `remove_status` sibling in this SAME resolution batch (like `drop_prepare` just above) so
  // the reservation is consumed and undoes/redoes as one atomic unit with the attack. The
  // single-use consume + the trigger clause's own 1/round cadence cap together enforce RAW's
  // "1/round" with no stack/replace path. `intent.consumeReservation` was server-stamped by the
  // room's match gate.
  if (intent.type === "request_attack" && intent.consumeReservation !== undefined) {
    siblings.push(...consumeStatusCommands(actorId, [{ kind: "consume_status", carrier: intent.attacker, status: intent.consumeReservation }], loop));
  }
  if (intent.type === "request_barrage" && locks) {
    const firedBefore = lock?.weapons ?? [];
    // The lock holds the opener's committed mounts, unchanged by later shots.
    const mounts = midAttack ? [...(lock?.mounts ?? [])] : [...(intent.mounts ?? [])];
    const ordnanceWindow = midAttack ? lock?.ordnanceWindow === true : checkOrdnanceWindow(state, intent.attacker);
    siblings.push({
      type: "open_attack",
      action: "barrage",
      actorId,
      combatantId: intent.attacker,
      weapon: intent.weapon,
      mounts,
      ...(!midAttack ? { ordnanceWindow } : {}),
    });
    const after = { weapons: [...firedBefore, intent.weapon], mounts, ordnanceWindow };
    if (!hasLegalBarrageContinuation(state, intent.attacker, after)) {
      siblings.push({ type: "close_attack", actorId, combatantId: intent.attacker });
    }
  }
  if (intent.type === "request_full_tech" && locks) {
    // Full Tech's open/close fold — mirrors Barrage's just above, but simpler: no Ordnance-window
    // snapshot (irrelevant to tech options)
    // and the auto-close predicate is simply "2 fired," no weapon-eligibility complexity to weigh.
    const firedBefore = lock?.weapons ?? [];
    siblings.push({ type: "open_attack", action: "full_tech", actorId, combatantId: intent.actor, weapon: intent.option });
    const firedAfter = [...firedBefore, intent.option];
    if (firedAfter.length >= 2) {
      siblings.push({ type: "close_attack", actorId, combatantId: intent.actor });
    }
  }
  if (intent.type === "request_attack" && !intent.reaction && locks && intent.weapon !== undefined) {
    if (midAttack) {
      siblings.push({ type: "open_attack", action: "skirmish", actorId, combatantId: intent.attacker, weapon: intent.weapon });
      // Close once nothing is left to fire: the Aux rider ends it, a multi-attack weapon's last attack does.
      const after = {
        weapons: [...(lock?.weapons ?? []), intent.weapon],
        ...(lock?.mounts !== undefined ? { mounts: lock.mounts } : {}),
        ordnanceWindow: lock?.ordnanceWindow === true,
      };
      if (!hasLegalSkirmishContinuation(state, intent.attacker, after)) {
        siblings.push({ type: "close_attack", actorId, combatantId: intent.attacker });
      }
    } else if (hasEligibleSkirmishRider(state, intent.attacker, intent.weapon)) {
      // A Skirmish commits to its first weapon's mount (`decisions/0049`, 2026-09-24 amendment).
      const mounts = [mountKeyOf(state.combatants[intent.attacker]?.base.mounts, intent.weapon)];
      siblings.push({ type: "open_attack", action: "skirmish", actorId, combatantId: intent.attacker, weapon: intent.weapon, mounts, ordnanceWindow: checkOrdnanceWindow(state, intent.attacker) });
    }
  }
  // A forced action is still the action it names: its `on_action_taken` triggers fire on or off turn.
  const actionTakenId: string | null =
    intent.type === "request_overcharge" && acting ? "overcharge" : (spend || policy.kind === "forced") && actionId !== null ? actionId : null;
  // The before-attack fires ride at the head of the primary entry, so the room still reads entry 0
  // as the attack and one undo takes back both.
  const preCommands = prePass?.plan.entries.map((e) => e.command) ?? [];
  // A forced resolution says so on its logged command, so the retained log and undo feed show it.
  const primary = policy.kind === "forced" ? { ...result.command, forced: true as const } : result.command;
  const command: EncounterCommand = siblings.length > 0 || preCommands.length > 0 ? { type: "batch", commands: [...preCommands, primary, ...siblings] } : primary;

  // ── Apply the primary resolution + siblings, then the eager followup chain — each its own ──
  // ── plan entry, in the SAME order the room used to apply+broadcast them. ──
  const exec = createScratchExecutor(state);
  // The same raw map `on_hit`/`on_crit` `choose` answers ride in (`RequestAttack.choices` and its
  // per-intent mirrors); threaded into `fireActionTaken` below too, so a non-boost `on:"action"`
  // `requiresOptIn` carrier reachable through THIS mirror (not just Boost's `refereeCommand.ts`
  // path) can be answered.
  const intentChoices = "choices" in intent ? intent.choices : undefined;
  // EVERY answered cross-actor key, not just the first (`area-attack-reaction-offers` D4): an area
  // attack fans out over several caught targets and each one's Brace is its own answered prompt, so
  // a `.find` left every stale copy but the first one pinned on the GM's screen. `.filter` + the
  // direction id is the full set the auto-dismiss needs. The two sources can't collide — a
  // direction key is `move_toward:`/`forced_move_toward:`, which `isCrossActorAnsweredPrompt`
  // rejects (neither a reaction-offer id nor the `<id>#<n>` authored shape).
  //
  // Read the answered direction promptId straight off `result` (the engine's own report —
  // {@link Resolved.answeredDirectionPromptId}) rather than hand-reconstructing
  // `move_toward:${intent.target.id}`. That reconstruction silently assumes the mover is always
  // the intent's TARGET (true for Ace's on-miss reposition, false for a declared-phase self-lunge
  // like `t_siege_specialist:2`/`t_hunter:1`, where the mover is the ATTACKER) and never
  // recognizes the `forced_move_toward:` namespace (`t_hacker:1`) at all — `resolve()` mints both,
  // so it is the one place that can report which was actually answered.
  const answeredPrompts = [
    ...Object.keys(intentChoices ?? {}).filter(isCrossActorAnsweredPrompt),
    ...(result.answeredDirectionPromptId !== undefined ? [result.answeredDirectionPromptId] : []),
  ];
  // The `about` breadcrumb (`resolution-narration` 1.1) — `cost.actor` is already the acting
  // COMBATANT id (not the slot `actorId` above), and `actionId` (computed above for the
  // once-per-turn ledger gate) doubles as the lowercase action verb: both are facts this
  // function already derived for other reasons, read again here rather than re-derived.
  const aboutWeapon = aboutWeaponFor(state, intent, cost.actor);
  const aboutTargets = aboutTargetsFor(intent);
  // The two BARRAGE-shot facts (`resolution-narration` 2.2), both read off the resolved command
  // this function already holds rather than re-derived:
  //   - `barrage` off the resolved command's own `kind` field, which the `request_barrage`
  //     branch above relabelled;
  //   - `noBonus` off the Aux-rider marker the `isRiderShot` block above already stamped
  //     (that block runs earlier in this same function, so the flag is settled by now).
  // `about` is the channel rather than the rider's own `kind`/a new rider field because it is the
  // only one that reaches BOTH narrator surfaces — `cli/src/encounter-session.ts`'s
  // `EncounterResult` captures `about` and not `kind` — and because it is already per-viewer
  // projected and redaction-aware. See `ResolutionAbout.barrage`'s doc comment (`steps.ts`).
  const aboutBarrage = result.command.kind === "barrage";
  const aboutNoBonus = result.command.noBonus === true;
  const aboutShot = aboutShotFor({ siblings, midAttack, lock });
  const aboutTechOption = aboutTechOptionFor({ state, intent, overlay, steps: result.command.steps });
  // A reaction attack a rule grants (Impending Threat, Moving Target) names its feature as the item,
  // so the card and the feed say which reaction it was; Covering Fire keeps its own receipt row.
  const shotGrant = intent.type === "request_attack" && cost.reaction ? reactionShotGrant(state, intent) : undefined;
  const aboutItem = aboutItemFor(state, intent, overlay) ?? (shotGrant !== undefined && shotGrant.reactionId !== COVERING_FIRE_REACTION ? { name: shotGrant.name, id: shotGrant.reactionId } : undefined);
  // A non-attack resolution with no ledger action id (Overcharge) records its kind, like every
  // head-less rider (`headlessAbout`). An attack or reaction with none keeps `action` absent.
  const isAttackKind = result.command.kind === "attack" || result.command.kind === "barrage";
  const aboutAction = actionId ?? (cost.reaction || isAttackKind ? undefined : result.command.kind);
  const about: ResolutionAbout = {
    actor: cost.actor,
    ...(aboutAction !== undefined ? { action: aboutAction } : {}),
    ...(aboutItem !== undefined ? { item: aboutItem } : {}),
    ...(aboutWeapon !== undefined ? { weapon: aboutWeapon } : {}),
    ...(aboutTargets !== undefined && aboutTargets.length > 0 ? { targets: aboutTargets } : {}),
    ...(aboutTechOption !== undefined ? { techOption: aboutTechOption } : {}),
    ...(aboutBarrage ? { barrage: true as const } : {}),
    ...(aboutShot !== undefined ? { shot: aboutShot } : {}),
    ...(aboutNoBonus ? { noBonus: true as const } : {}),
    ...(pairedAuxRider !== undefined ? { auxRider: aboutAuxRiderFor(state, cost.actor, pairedAuxRider) } : {}),
    ...(intent.type === "request_attack" && cost.reaction && cost.reactionId !== undefined ? { reaction: cost.reactionId } : {}),
    ...(policy.kind === "forced" ? { forced: true as const } : {}),
  };
  const warnings = result.warnings;
  const resolution = {
    steps: [...(prePass?.steps ?? []), ...result.command.steps],
    kind: result.command.kind,
    actorId,
    about,
    ...(answeredPrompts.length > 0 ? { promptIds: answeredPrompts } : {}),
    ...(result.areaOrigin !== undefined ? { areaOrigin: result.areaOrigin } : {}),
    ...(warnings.length > 0 ? { warnings } : {}),
  };
  const madeAttackOrSave = result.command.steps.some((s) => s.op === "to_hit" || s.op === "save");
  const attacker = cost.actor;
  const destroyedSomething = stepsLandDestroy(result.command.steps);
  const movedIds = stepsMovePosition(result.command.steps);
  // Post-resolve auto-reveal sweep — computed off the
  // PRIMARY resolution's own steps, applied as its own plan entry right after the primary
  // apply; the ids it reveals ride the primary entry's rider so the room's `revealed`
  // accumulator (seeded by the pre-dice reveal, already run room-side) sees them before its
  // `broadcastResolution` feed-suppression check.
  const revealCandidates = stepsCombatantIds(result.command.steps);

  const primaryEvents = unwrap(exec.apply(command, actorId, undefined));
  // A status its carrier's taking this resolution's damage ends (Active Camouflage's Invisible).
  endOnDamageInto(exec, { actorId, dice: loop.dice, overlay, loop }, damageWitnessReports(result.command.steps));
  // **A candidate id must have EXISTED before this resolution ran** (`state`, the pre-apply
  // snapshot) to be eligible for the sweep. A combatant this SAME resolution just spawned (a
  // `deploy_mine` atom's own `spawn_object` step names the mine's OWN id, and `spawnMine`
  // deliberately spawns it `concealed: true`) must never be auto-revealed by the mere act of
  // appearing in its own spawn step — without this guard, deploying a mine self-reveals it in the
  // SAME activation that hid it. An id that pre-existed (an ambush unit touched/hit/moved by this
  // resolution) is unaffected: it's still swept and revealed.
  const toReveal = [...new Set(revealCandidates.filter((id): id is string => id !== undefined))].filter(
    (id) => state.combatants[id] !== undefined && exec.state.combatants[id]?.concealed === true,
  );
  const primaryRider: ResolutionBroadcastRider = { kind: "resolution", resolution, revealedIds: toReveal };
  if (toReveal.length > 0) {
    const reveals: EncounterCommand[] = toReveal.map((id) => ({ type: "set_concealed", actorId, combatantId: id, concealed: false }));
    const revealCmd: EncounterCommand = reveals.length === 1 ? reveals[0]! : { type: "batch", commands: reveals };
    applyTrusted(exec, revealCmd, actorId);
  }
  if (madeAttackOrSave) raiseAttackResolved(exec, { actorId, attacker, loop });
  // A folded forced attack (Aggression's swing, Rebound's reflection) was made by someone else.
  for (const forced of forcedAttackersIn(result.command.steps)) if (forced !== attacker) raiseAttackResolved(exec, { actorId, attacker: forced, loop });
  if (destroyedSomething) spawnDestructionConsequences(exec, actorId);
  // The walker's own recompute is its `moved`'s, below, with the walk's origin as `from` (D10).
  const displaced = result.movePath !== undefined ? movedIds.filter((id) => id !== result.movePath!.mover) : movedIds;
  if (displaced.length > 0) runPostMoveRecomputeForIds(exec, actorId, displaced);
  // A resolution's own walk (Charge, Impale, Avalanche Charge, Puppet System's walk of its target)
  // runs the hazards a `request_move` runs, in `refereeMove`'s order: the move trigger family (its
  // event `voluntary` unless the walk was someone else's), dangerous terrain (Core p.63), the mine
  // proximity trigger (Core p.107, `from` = the path's origin), then Trample. A knockback or a
  // pull is no walk: involuntary displacement still skips terrain and mines, the accepted gap in
  // the `dangerous-terrain`/`drones-and-mines` trackers. A walk that went nowhere entered nothing,
  // so it skips the hazards (the end-of-turn terrain check still covers standing in it). Who stood
  // on the path is read off the pre-resolution state: Impale's pull may have moved them since.
  // The followups draw off the resolution's own recording source: the observed-move window below
  // can halt, and its echo must carry every die the hazards drew before it (`decisions/0164` D1).
  const followupCtx = { actorId, dice: loop.dice, overlay, loop };
  if (result.movePath !== undefined) {
    const { mover, path } = result.movePath;
    const origin = path[0];
    const end = exec.state.combatants[mover]?.pos ?? undefined;
    const wentSomewhere = origin !== undefined && end !== undefined && !worldFor(exec.state).equals(origin, end);
    const family = wentSomewhere ? { from: origin, to: end, path, spaces: path.length - 1, ...(result.movePath.voluntary === false ? { voluntary: false as const } : {}) } : undefined;
    // One chain (D10): a walk is movement, so its recompute drags (it has `from`), it bulldozes and its
    // movement ends (`movement_ended`); a walk that went nowhere entered nothing.
    runMovement({
      moved: { exec, ctx: followupCtx, mover, from: origin, path, by: "walk", ...(family !== undefined ? { family } : {}), hazards: wentSomewhere, crossedIn: state },
      endsMovement: wentSomewhere,
    });
    // The watchers' reactions to the walk (Rapid Response: "an enemy character in line of sight
    // moves"), as a `request_move`'s end offers them, for a walk that provokes reactions (Impale,
    // Strafe) the way the Overwatch checkpoint above gathers only for one: Charge and Avalanche
    // Charge "ignore reactions". An unanswered offer halts the whole resolution, which re-runs on
    // the answer with its recorded dice; an accepted reaction move then asks its landing
    // (`askPendingReactionMove`).
    if (origin !== undefined && end !== undefined && !worldFor(exec.state).equals(origin, end) && result.movePath.provokesReactions === true) {
      try {
        observedMoveInto(exec, { ...followupCtx, choices: intentChoices, interactive: true }, { before: state, move: { moverId: mover, from: origin, to: end, path } });
      } catch (err) {
        if (!(err instanceof NeedsDecisionSignal)) throw err;
        const halt: NeedsDecision = { kind: "needs_decision", who: err.who ?? mover, promptId: err.promptId, prompt: err.prompt, options: err.options, ...(err.title !== undefined ? { title: err.title } : {}), ...(err.answerKind !== undefined ? { answerKind: err.answerKind } : {}) };
        const echoedIntent = { ...intent, recordedDice: recordingDice.log() };
        return needsDecisionPlan(state, halt, echoedIntent);
      }
    }
  }
  // Drone fields: a deployed or redeployed drone lands its field, and the resolution's other moves
  // (a push, a pull, a redeploy) are entry checks between their endpoints (`./auraFollowups.ts`).
  auraAfterResolutionInto(exec, followupCtx, { steps: result.command.steps, ...(result.movePath !== undefined ? { walked: result.movePath.mover } : {}) });
  if (actionTakenId !== null) {
    // A targeted Quick Tech (Lock On, Scan, Bolster) binds the fired clause's `target` to its target.
    const techTarget = intent.type === "request_activate" && QUICK_TECH_ACTIVATE_IDS.has(intent.action) ? intent.target : undefined;
    fireActionTaken(exec, ctx, { actorId, carrierId: attacker, actionId: actionTakenId, verb: earmarkVerbOf(intent, { state }), primaryTarget: techTarget, choices: intentChoices });
  }
  for (const decline of provoked.declines) unwrap(exec.apply(decline, actorId, undefined));

  const { entries, finalState } = exec.finish();
  // Stamp the primary entry's rider (the FIRST recorded entry — `exec.apply`'s own doc: one
  // `.apply()` call is one plan entry, in application order).
  const stamped: RefereePlanEntry[] = entries.map((e, i) => (i === 0 ? { ...e, broadcastRider: primaryRider } : e));
  // `primaryEvents` is unused beyond confirming the trusted apply succeeded — the room re-derives
  // its OWN events from its real re-apply of `entries[0].command`.
  void primaryEvents;
  return { entries: stamped, finalState };
}
